import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import axios from 'axios';
import TachesBanner from '../components/admin/TachesBanner';
import { installerCacheHorsLigne } from '../offlineCache';

installerCacheHorsLigne();

// Iter 298 — 📡 Bandeau « hors ligne — lecture seule » (dernières données chargées).
const BandeauHorsLigne = () => {
  const [hors, setHors] = useState(typeof navigator !== 'undefined' ? !navigator.onLine : false);
  const [lectureAt, setLectureAt] = useState(null);
  useEffect(() => {
    const on = () => { setHors(false); setLectureAt(null); };
    const off = () => setHors(true);
    const lect = (e) => { setHors(true); setLectureAt(e.detail?.t || null); };
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    window.addEventListener('raav-offline-lecture', lect);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
      window.removeEventListener('raav-offline-lecture', lect);
    };
  }, []);
  if (!hors) return null;
  return (
    <div className="sticky top-0 z-[60] bg-red-600 text-white text-xs font-bold px-4 py-2 text-center" data-testid="admin-offline-banner">
      📡 Hors ligne — consultation en LECTURE SEULE des derniers écrans chargés
      {lectureAt ? ` (données du ${new Date(lectureAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })})` : ''}.
      Le pointage et les encaissements Jour J restent possibles (synchronisés au retour du réseau).
    </div>
  );
};

// Iter 155 (Kamel — perf admin) — Lazy loading des onglets
// Chaque onglet est téléchargé UNIQUEMENT au moment où l'utilisateur clique
// dessus. Gain bundle initial : ~3 MB → ~400 KB. Premier affichage 5-10× plus rapide.
// Les chunks rarement utilisés (GoogleAudit, BulkImport...) ne sont JAMAIS
// téléchargés dans une session normale.
const GestionVoyages = lazy(() => import('./GestionVoyages'));
const GoogleContactsSync = lazy(() => import('../components/admin/GoogleContactsSync'));

// Iter 532 (Kamel) — onglets du quotidien (toujours visibles) ; le reste est replié sous « Outils avancés »
const QUOTIDIEN = new Set(['accueil', 'communication', 'registrations', 'gestion', 'permanences', 'bus_plans', 'espagne', 'espace', 'prospection', 'depenses', 'caisse', 'tresorerie', 'trips', 'feuille_route', 'news', 'taches', 'staff']);
// Petit rappel « à quoi sert cet onglet » (masquable)
const TAB_HELP = {
  registrations: "Le quotidien : cherche un adhérent (nom, prénom, téléphone, voyage…), encaisse, prends RDV. Permanence et caisse du jour repliées juste en dessous.",
  gestion: "Tout un voyage sur un écran : inscrits, chambres, bus, finances, tâches et documents. Un classeur par voyage.",
  permanences: "Rendez-vous de la permanence : pris par les adhérents (rhoneamitievoyages.fr/moi) ou pour eux, planning, venu/absent, relances, horaires & indisponibilités, agenda Google.",
  bus_plans: "Plan de bus par voyage : placement, inscriptions du jour (fiches liées), export pour le chauffeur.",
  espagne: "Lloret 2027 : places par semaine (chambres hôtel, sans transport, car aller, car retour), tarifs, horaires des cars, coûts hôtel, devis, publication.",
  espace: "Tout l'espace adhérent au même endroit : notes & formalités, rubriques (pour tous ou par voyage), sondages, checklist départ, notifications push, stats.",
  prospection: "Liste de diffusion, annonces de voyage, campagnes : faire connaître les prochains départs.",
  depenses: "Dépenses par voyage, échéancier fournisseurs (acomptes / soldes) — relié aux tâches.",
  caisse: "Encaissements physiques (espèces, chèques, CB) à rapprocher de la caisse réelle.",
  tresorerie: "Vue globale banque / caisse / à venir : où en est l'association.",
  trips: "Fiches voyage publiées sur le site : textes, photos, prix, publication.",
  feuille_route: "Modèle de la feuille de route appliqué à chaque nouveau voyage (tâches auto).",
  news: "Actualités affichées sur le site.",
  accueil: "Page d'accueil : bloc affiches (Espagne 2027…) — afficher/masquer, textes, bouton, images.",
  communication: "Réseaux sociaux : textes TikTok / Facebook / WhatsApp, générateur IA, visuels, vidéo spa, liens comptés, plan de lancement.",
  taches: "Ton coach : tâches par voyage, générales et perso, agenda de la semaine, série de victoires.",
  coherence: "Contrôle de cohérence global (catalogue, fiches, finances) : à lancer après un gros import ou avant un départ.",
  controle: "Centre de contrôle : chaîne complète (voyage → inscription → paiement → facture → TVA → compta → espace adhérent), audits voyages, financier, compta et tests techniques.",
  audit: "Journal des actions admin : qui a fait quoi, quand.",
  staff: "Bénévoles et accompagnateurs : liens magiques et droits limités.",
  access: "Comptes et accès au back-office.",
  coffre_fort: "Documents sensibles chiffrés (contrats, identifiants).",
  google_contacts: "Synchronisation des inscrits vers Google Contacts (groupes par voyage). Outil ponctuel.",
  stats: "Chiffres clés des inscriptions par voyage et par période.",
  sondages: "Sondages envoyés aux adhérents et résultats.",
  urgent_reminders: "Relances urgentes (retards de paiement, pièces manquantes) et saisies voyageurs à contrôler.",
  embarquement: "Scan des billets bus le jour du départ (accompagnateurs).",
  destination_notes: "Notes et questions par destination, visibles par les voyageurs dans leur espace.",
  tel_partages: "Qualité des fiches : dates de naissance douteuses, téléphones partagés entre plusieurs fiches (foyers), contacts.",
  marketing: "Outils marketing avancés (affiches, visuels, textes IA).",
  sante_fiscale: "Santé fiscale de l'association : seuils, TVA, alertes.",
  moyens_paiement: "Moyens de paiement proposés dans l'Espace Adhérent : IBAN, lien carte, PayPal, courrier — activer / bloquer pour tous, ou discrètement par voyage / par fiche.",
  cheques_attente: "Chèques reçus non encore remis en banque.",
  remises: "Remises bancaires (bordereaux de chèques).",
  pointage_banque: "Rapprochement des lignes bancaires (outil avancé).",
  prelevements: "Prélèvements et paiements en ligne reçus.",
  import_recettes: "Import en masse d'historiques de règlements (Excel).",
  rattrapage_express: "Saisie rapide d'anciennes inscriptions papier.",
  ancv: "Chèques-vacances ANCV : suivi et remises.",
  association: "Adhésions et cotisations à l'association.",
  annulations: "Annulations et frais retenus.",
  bilan: "Bilan financier par voyage et par exercice.",
  suivi_tva: "Suivi de la TVA sur marge (outil avancé).",
  audit_compta: "Audit comptable : incohérences entre règlements, dépenses et banque.",
  corbeille: "Voyages supprimés, restaurables.",
  testimonials: "Témoignages affichés sur le site.",
  visuels_ia: "Studio visuel IA : visuels sobres (carré / story) pour les actualités et réseaux sociaux. Président uniquement.",
  messages: "Messages reçus via le formulaire de contact.",
  brouillons: "Formulaires d'inscription commencés mais jamais envoyés (avec un e-mail saisi) : relance automatique par e-mail à 2 h puis à J+2, et relance à la main par WhatsApp ici.",
  form_textes: "Les phrases du formulaire d'inscription, de l'espace adhérent et des e-mails automatiques (confirmation, rappels) : modifie-les ici, vide = texte standard.",
  assistant_ia: "Assistant IA : rédaction, réponses types, aide au quotidien.",
  excursions: "Extras et excursions vendus en plus des voyages.",
};
const RegistrationsTab = lazy(() => import('../components/admin/RegistrationsTab'));
const ProspectionTab = lazy(() => import('../components/admin/ProspectionTab'));
const TestimonialsTab = lazy(() => import('../components/admin/TestimonialsTab'));
const VisuelsIATab = lazy(() => import('../components/admin/VisuelsIATab'));
const AssociationTab = lazy(() => import('../components/admin/AssociationTab'));
const BilanAnnuelTab = lazy(() => import('../components/admin/BilanAnnuelTab'));
const SuiviTvaTab = lazy(() => import('../components/admin/SuiviTvaTab'));
const CentreControleTab = lazy(() => import('../components/admin/CentreControleTab'));
const FormTextesPanel = lazy(() => import('../components/admin/FormTextesPanel').then(m => ({ default: m.FormTextesPanel })));
const BrouillonsPanel = lazy(() => import('../components/admin/BrouillonsPanel').then(m => ({ default: m.BrouillonsPanel })));
const EspagnePanel = lazy(() => import('../components/admin/EspagnePanel').then(m => ({ default: m.EspagnePanel || m.default })));
const AccueilAffichesTab = lazy(() => import('../components/admin/AccueilAffichesTab'));
const CommunicationTab = lazy(() => import('../components/admin/CommunicationTab'));
// Iter 569 (Kamel) — anciens onglets d'audit regroupés dans « 🩺 Centre de contrôle » (liens ?tab= conservés)
const CONTROLE_SUBS = { coherence: 'voyages', audit: 'financier', audit_compta: 'compta', sante: 'tests' };
const AccessCodesTab = lazy(() => import('../components/admin/AccessCodesTab'));
const StaffDirectoryTab = lazy(() => import('../components/admin/StaffDirectoryTab'));
const StatsTab = lazy(() => import('../components/admin/StatsTab'));
const DepensesTab = lazy(() => import('../components/admin/DepensesTab'));
const ImportRecettesTab = lazy(() => import('../components/admin/ImportRecettesTab'));
const RattrapageExpressTab = lazy(() => import('../components/admin/RattrapageExpressTab'));
const AssistantIATab = lazy(() => import('../components/admin/AssistantIATab'));
const TvaTrimestreBanner = lazy(() => import('../components/admin/TvaTrimestreBanner'));
const CashRegisterTab = lazy(() => import('../components/admin/CashRegisterTab'));
const AncvRegisterTab = lazy(() => import('../components/admin/AncvRegisterTab'));
const PendingChequesTab = lazy(() => import('../components/admin/PendingChequesTab'));
const RemisesTab = lazy(() => import('../components/admin/RemisesTab'));
const PermanencesTab = lazy(() => import('../components/admin/PermanencesTab'));
const MessageTemplatesTab = lazy(() => import('../components/admin/MessageTemplatesTab'));
const UrgentRemindersTab = lazy(() => import('../components/admin/UrgentRemindersTab'));
const BusPlansAdmin = lazy(() => import('../components/admin/BusPlansAdmin'));
const EmbarquementTab = lazy(() => import('../components/admin/EmbarquementTab'));
const FlightInfoModal = lazy(() => import('../components/admin/FlightInfoModal'));
const EmailMarketingAdmin = lazy(() => import('../components/admin/EmailMarketingAdmin'));
const FormDestinationsTab = lazy(() => import('../components/admin/FormDestinationsTab'));
const TripCatalogueSection = lazy(() => import('../components/admin/TripCatalogueSection'));
const BulkImportTab = lazy(() => import('../components/admin/BulkImportTab'));
const CoffreFortTab = lazy(() => import('../components/admin/CoffreFortTab'));
const PointageBanqueTab = lazy(() => import('../components/admin/PointageBanqueTab'));
const DashboardFiscalTab = lazy(() => import('../components/admin/DashboardFiscalTab'));
const VoyagesAnnulesTab = lazy(() => import('../components/admin/VoyagesAnnulesTab'));
const PrelevementsTab = lazy(() => import('../components/admin/PrelevementsTab'));
const TrashTripsTab = lazy(() => import('../components/admin/TrashTripsTab'));
const DinoTab = lazy(() => import('../components/admin/DinoTab'));
const ExcursionsTab = lazy(() => import('../components/admin/ExcursionsTab'));
const EspaceAdherentTab = lazy(() => import('../components/admin/EspaceAdherentTab'));
// Iter 552 (Kamel) — anciens onglets regroupés dans « Espace adhérent » (liens ?tab= conservés)
// Iter 761 (Kamel) — éditeurs « 1 ligne = 1 élément » pour les blocs de la page voyage (autonomie complète)
const depToText = (arr) => (arr || []).map(d => [d.time || '', d.city || '', d.address || '', d.note || ''].join(' | ').replace(/( \| )+$/, '')).join('\n');
const textToDep = (txt) => String(txt || '').split('\n').map(l => l.trim()).filter(Boolean).map(l => { const [time = '', city = '', address = '', note = ''] = l.split('|').map(x => x.trim()); return note ? { time, city, address, note } : { time, city, address }; });
const actToText = (arr) => (arr || []).map(a => [a.name || '', a.desc || ''].join(' | ').replace(/ \| $/, '')).join('\n');
const textToAct = (txt) => String(txt || '').split('\n').map(l => l.trim()).filter(Boolean).map(l => { const [name = '', desc = ''] = l.split('|').map(x => x.trim()); return { name, desc }; });
const linesToArr = (txt) => String(txt || '').split('\n').map(x => x.trim()).filter(Boolean);
const ESPACE_SUBS = { destination_notes: 'notes', sondages: 'sondages', checklist_depart: 'checklist', espace_rubriques: 'rubriques', espace_push: 'push', espace_stats: 'stats' };
const TelephonesPartagesTab = lazy(() => import('../components/admin/TelephonesPartagesTab'));
const TachesTab = lazy(() => import('../components/admin/TachesTab'));
// Modals utilisés depuis GestionVoyages - imports statiques OK (petits + fréquents)
import DejaPayeAlerts from '../components/admin/DejaPayeAlerts';
import { CompteursBloc } from '../components/admin/CompteursEspagne';
import TripDeletionModal from '../components/admin/TripDeletionModal';
import { ItineraryEditor, HotelsEditor, NotesEditor, ProgrammeImportModal } from '../components/admin/TripProgramEditor';
import { REPAS_OPTIONS, formuleRepasLabel } from '../utils/repas';
import { TripPhotosEditor } from '../components/admin/TripPhotosEditor';
import { TripCarteEditor } from '../components/admin/TripCarteEditor';
const FeuilleDeRouteTab = lazy(() => import('../components/admin/FeuilleDeRouteTab'));
const TresorerieTab = lazy(() => import('../components/admin/TresorerieTab'));
import AssistantVoyage from '../components/admin/AssistantVoyage';
import Repli from '../components/admin/Repli';
import { VoirCommeAdherentBtn } from '../components/admin/RubriquesPerso';
import AdminErrorBoundary from '../components/AdminErrorBoundary';
import MoyensPaiementTab from '../components/admin/MoyensPaiementTab';
import { ADMIN_PATH } from '../adminPath';
import NouveautesBtn from '../components/admin/NouveautesBtn';
import ExcursionsBlocEditor from '../components/admin/ExcursionsBlocEditor';
import DatesVoyageAdmin from '../components/admin/DatesVoyageAdmin';

// Iter 155 — Spinner RAAV pendant le téléchargement d'un onglet
const TabLoadingFallback = () => (
  <div className="flex flex-col items-center justify-center py-20" data-testid="admin-tab-loading">
    <div className="relative w-12 h-12 mb-4">
      <div className="absolute inset-0 rounded-full border-4 border-[#5BC5D4]/20"></div>
      <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-[#5BC5D4] animate-spin"></div>
    </div>
    <p className="text-sm text-gray-600 font-semibold">Chargement de l&apos;outil…</p>
    <p className="text-[10px] text-gray-400 mt-1">Rhône Alpes Amitié Voyages</p>
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Iter 587 (Kamel — sécurité) : identifiant d'appareil stable → journal des connexions + alerte « nouvel appareil ».
const deviceId = () => {
  let id = localStorage.getItem('admin_device_id');
  if (!id) {
    id = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem('admin_device_id', id);
  }
  return id;
};
const secuHeaders = (type) => ({ 'X-Device-Id': deviceId(), 'X-Login-Type': type });

const WEEK_LABELS = {
  '14mai-sans': '14-17 mai (sans transport)',
  '14mai-avec': '14-17 mai (avec transport)',
  '23mai-sans': '23-26 mai (sans transport)',
  '23mai-avec': '23-26 mai (avec transport)',
  '4juil-sans': '4-11 juillet (sans transport)',
  '11juil-sans': '11-18 juillet (sans transport)',
  '18juil-sans': '18-25 juillet (sans transport)',
  '18juil-avec': '18-25 juillet (avec transport)',
  '25juil-sans': '25 juil - 1 août (sans transport)',
  '25juil-avec': '25 juil - 1 août (avec transport)',
  '1aout-sans': '1-8 août (sans transport)',
  '1aout-avec': '1-8 août (avec transport)',
  '8aout-sans': '8-15 août (sans transport)',
  '8aout-avec': '8-15 août (avec transport)',
  '15aout-sans': '15-22 août (sans transport)',
  '15aout-avec': '15-22 août (avec transport)',
  '22aout-sans': '22-29 août (sans transport)',
  '29aout-sans': '29 août - 5 sept (sans transport)',
  // Iter 674 — Espagne 2027
  '6mai-2027-sans': 'Ascension 6-9 mai 2027 (sans transport)', '6mai-2027-avec': 'Ascension 6-9 mai 2027 (avec transport)',
  '15mai-2027-sans': 'Pentecôte 15-18 mai 2027 (sans transport)', '15mai-2027-avec': 'Pentecôte 15-18 mai 2027 (avec transport)',
  '3juil-2027-sans': '3-10 juillet 2027 (sans transport)', '10juil-2027-sans': '10-17 juillet 2027 (sans transport)',
  '17juil-2027-sans': '17-24 juillet 2027 (sans transport)', '17juil-2027-avec': '17-24 juillet 2027 (avec transport)',
  '24juil-2027-sans': '24-31 juillet 2027 (sans transport)', '24juil-2027-avec': '24-31 juillet 2027 (avec transport)',
  '31juil-2027-sans': '31 juil - 7 août 2027 (sans transport)', '31juil-2027-avec': '31 juil - 7 août 2027 (avec transport)',
  '7aout-2027-sans': '7-14 août 2027 (sans transport)', '7aout-2027-avec': '7-14 août 2027 (avec transport)',
  '14aout-2027-sans': '14-21 août 2027 (sans transport)', '14aout-2027-avec': '14-21 août 2027 (avec transport)',
  '21aout-2027-sans': '21-28 août 2027 (sans transport)', '28aout-2027-sans': '28 août - 4 sept 2027 (sans transport)',
};

// Ordre chronologique des semaines
const WEEK_ORDER = [
  '14mai-sans', '14mai-avec', '23mai-sans', '23mai-avec',
  '4juil-sans', '11juil-sans',
  '18juil-sans', '18juil-avec', '25juil-sans', '25juil-avec',
  '1aout-sans', '1aout-avec', '8aout-sans', '8aout-avec',
  '15aout-sans', '15aout-avec', '22aout-sans', '29aout-sans',
  '6mai-2027-sans', '6mai-2027-avec', '15mai-2027-sans', '15mai-2027-avec',
  '3juil-2027-sans', '10juil-2027-sans', '17juil-2027-sans', '17juil-2027-avec', '24juil-2027-sans', '24juil-2027-avec',
  '31juil-2027-sans', '31juil-2027-avec', '7aout-2027-sans', '7aout-2027-avec', '14aout-2027-sans', '14aout-2027-avec',
  '21aout-2027-sans', '28aout-2027-sans',
];

// Iter 149d — Bannière hebdo pour le profil bénévole (Messa) — validée par Kamel.
// Affichée 1 fois par semaine (lundi → dimanche), 5 messages qui tournent,
// uniquement pour role='limited'. Fermable, mémorisée dans localStorage avec
// la clé hebdomadaire "raav_msg_messa_<ISOweek>".
const MESSA_MESSAGES = [
  { emoji: '🌟', text: 'Bienvenue Messa, prête pour de nouveaux dossiers ?', sub: 'Bon courage et merci pour ton implication 💪' },
  { emoji: '💙', text: 'Belle semaine Messa, on compte sur toi !', sub: 'Chaque dossier saisi rend les voyageurs heureux 🌍' },
  { emoji: '🚀', text: 'Tu es au top Messa, comme toujours !', sub: 'Merci pour ta rigueur et ta bonne humeur 😊' },
  { emoji: '🎯', text: 'Bonne semaine de saisie Messa !', sub: 'L\'équipe RAAV te dit un grand merci 🙏' },
  { emoji: '🌸', text: 'Hey Messa, ravi(e) de te revoir !', sub: 'Encore une belle semaine en perspective 💪' },
];

// ISO week number (UTC) — clé unique par semaine pour rotation et frequency
const _isoWeekKey = (d = new Date()) => {
  const target = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = (target.getUTCDay() + 6) % 7;
  target.setUTCDate(target.getUTCDate() - dayNum + 3);
  const firstThursday = new Date(Date.UTC(target.getUTCFullYear(), 0, 4));
  const week = 1 + Math.round(((target - firstThursday) / 86400000 - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7);
  return `${target.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
};

const WelcomeMessaBanner = ({ role }) => {
  // Visible UNIQUEMENT pour les comptes 'limited' (bénévole)
  const weekKey = _isoWeekKey();
  const stKey = `raav_msg_messa_${weekKey}`;

  const [visible, setVisible] = React.useState(() => {
    if (role !== 'limited') return false;
    try {
      return localStorage.getItem(stKey) !== '1';
    } catch {
      return true;
    }
  });

  if (!visible || role !== 'limited') return null;

  // Choix déterministe du message de la semaine (même message toute la semaine)
  const idx = parseInt(weekKey.slice(-2), 10) % MESSA_MESSAGES.length;
  const msg = MESSA_MESSAGES[idx];

  const dismiss = () => {
    try {
      localStorage.setItem(stKey, '1');
    } catch {
      /* noop */
    }
    setVisible(false);
  };

  return (
    <div
      className="bg-gradient-to-r from-[#1E3A5F] via-[#2d5a8f] to-[#5BC5D4] text-white shadow-lg relative"
      data-testid="welcome-messa-banner"
    >
      <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          <span className="text-3xl" aria-hidden>{msg.emoji}</span>
          <div>
            <p className="font-bold text-base sm:text-lg leading-tight">{msg.text}</p>
            <p className="text-xs sm:text-sm text-white/90 mt-0.5">{msg.sub} — l'équipe RAAV.</p>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="text-white/80 hover:text-white text-xl leading-none w-8 h-8 rounded-full hover:bg-white/10 transition flex items-center justify-center flex-shrink-0"
          title="Masquer ce message pour cette semaine"
          data-testid="welcome-messa-close"
        >
          ✕
        </button>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  // Iter 95 — Restauration de la session admin depuis localStorage au montage initial.
  // Avant : à chaque retour arrière ou rafraîchissement, on perdait le state username/password
  // et il fallait re-saisir les identifiants. Maintenant la session est persistée.
  const _restored = (() => {
    try {
      const auth = localStorage.getItem('admin_auth');
      if (!auth) return null;
      const decoded = atob(auth);
      const [u, ...pwParts] = decoded.split(':');
      let allowedTabs = [];
      try {
        const stored = localStorage.getItem('admin_allowed_tabs');
        if (stored) allowedTabs = JSON.parse(stored) || [];
      } catch { /* ignore */ }
      return {
        username: u, password: pwParts.join(':'),
        role: localStorage.getItem('admin_role') || 'full',
        allowedTabs,
      };
    } catch {
      return null;
    }
  })();
  const [isAuthenticated, setIsAuthenticated] = useState(!!_restored);
  const [username, setUsername] = useState(_restored?.username || '');
  const [password, setPassword] = useState(_restored?.password || '');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [role, setRole] = useState(_restored?.role || 'full');
  const [error, setError] = useState('');
  // Iter 97 - Restaurer l'onglet actif depuis localStorage pour éviter que les
  // window.location.reload() (PECA, tiers payeur, etc.) renvoient l'admin sur
  // l'onglet "Voyages" au lieu de rester sur "Inscriptions" / "Avoirs" / etc.
  const [showAvances, setShowAvances] = useState(false); // Iter 532 — outils avancés repliés
  const [helpHidden, setHelpHidden] = useState(() => { try { return localStorage.getItem('admin_help_hidden') === '1'; } catch { return false; } });
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const fromUrl = new URLSearchParams(window.location.search).get('tab');
      if (fromUrl) return fromUrl;
      const saved = localStorage.getItem('admin_active_tab');
      if (saved) return saved;
    } catch { /* ignore */ }
    return 'trips';
  });
  const [espaceSub, setEspaceSub] = useState(null);
  const [controleSub, setControleSub] = useState(null);
  // Iter 587 — le back-office ne doit jamais être indexé par les moteurs de recherche
  useEffect(() => {
    const m = document.createElement('meta');
    m.name = 'robots'; m.content = 'noindex, nofollow';
    document.head.appendChild(m);
    return () => { document.head.removeChild(m); };
  }, []);
  // Persist activeTab on every change
  useEffect(() => {
    if (ESPACE_SUBS[activeTab]) { setEspaceSub(ESPACE_SUBS[activeTab]); setActiveTab('espace'); return; }
    if (CONTROLE_SUBS[activeTab]) { setControleSub(CONTROLE_SUBS[activeTab]); setActiveTab('controle'); return; }
    try { localStorage.setItem('admin_active_tab', activeTab); } catch { /* ignore */ }
  }, [activeTab]);

  // Iter 135 — Si l'URL contient #search=xxx (deeplink depuis le rapport import bulk
  // du Plan du bus, ou tout autre module), on bascule automatiquement sur l'onglet
  // Inscriptions pour que la recherche soit visible. Re-vérifié à chaque login pour
  // gérer le cas "user pas encore loggé quand le hash arrive".
  useEffect(() => {
    if ((window.location.hash || '').includes('search=')) {
      setActiveTab('registrations');
    }
    const onHash = () => {
      if ((window.location.hash || '').includes('search=')) {
        setActiveTab('registrations');
      }
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [isAuthenticated]);
  // Iter 96i - allowed_tabs personnalises pour les comptes limited (cases a cocher)
  const [allowedTabs, setAllowedTabs] = useState(_restored?.allowedTabs || []);

  // Iter 257 — Garde-fou générique comptes limités : si l'onglet actif (restauré du
  // localStorage, potentiellement laissé par le compte président sur le même PC) n'est
  // PAS autorisé pour ce rôle → bascule sur le premier onglet autorisé. Corrige la
  // page blanche/fuite de données au rafraîchissement pour le compte bénévole.
  useEffect(() => {
    if (role !== 'limited') return;
    // Iter 328 — onglet unique (excursions OU embarquement seul coché) : accès direct, rien d'autre
    const soloTab = allowedTabs?.length === 1 && ['excursions', 'embarquement'].includes(allowedTabs[0]) ? allowedTabs[0] : null;
    const autorises = soloTab ? [soloTab] : ['registrations', ...(allowedTabs || [])];
    if (!autorises.includes(activeTab)) setActiveTab(autorises[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, allowedTabs, activeTab]);
  // Iter 96i - Compteurs d'actions en attente (badge sur Inscriptions)
  const [counters, setCounters] = useState({ overdue_payment: 0, missing_id: 0, missing_room: 0, total_pending: 0, pending_changes: 0 });
  // Iter 157i — quota emails Resend du jour (null = pas encore chargé)
  const [emailsToday, setEmailsToday] = useState(null);
  // Iter 157k — envois de masse programmés (demain 8h)
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [showScheduled, setShowScheduled] = useState(false);
  // Iter 157o (Kamel) — 🚨 pop-up soldes urgents (départ < 10 jours, reste > 0)
  const [urgentSoldes, setUrgentSoldes] = useState(null);
  const [showUrgentPopup, setShowUrgentPopup] = useState(false);
  // Iter 157q — 👻 alerte fantômes (scan nocturne automatique)
  const [fantomesAlert, setFantomesAlert] = useState(null);
  // Iter 158x — 🛂 badge saisies voyageurs à contrôler
  const [controleCount, setControleCount] = useState(0);

  const cancelScheduled = async (sid) => {
    if (!window.confirm('Annuler cet envoi programmé ?')) return;
    try {
      await axios.delete(`${API}/admin/emails/scheduled/${sid}`, { auth: { username, password } });
      setScheduledEmails((prev) => prev.filter((s) => s.id !== sid));
    } catch (e) {
      alert('Erreur : ' + (e.response?.data?.detail || e.message));
    }
  };
  const [trips, setTrips] = useState([]);
  const [loadingTrips, setLoadingTrips] = useState(false);
  const [tripsVues, setTripsVues] = useState({});
  const [news, setNews] = useState([]);
  const [loadingNews, setLoadingNews] = useState(false);
  const [showNewsForm, setShowNewsForm] = useState(false);
  const [newsForm, setNewsForm] = useState({ title: '', content: '', category: 'info', published: true });
  const [editingTrip, setEditingTrip] = useState(null);
  const [showTripForm, setShowTripForm] = useState(false);
  const [tripToDelete, setTripToDelete] = useState(null);
  const [tripForm, setTripForm] = useState({
    title: '', destination: '', description: '', transport: 'autocar',
    start_date: '', end_date: '', price_from: 0, price_info: '', available_spots: 0, total_spots: 0,
    image_url: '', program_pdf: '', program_details: '',
    highlights_text: '', included_text: '', not_included_text: '', gallery_images: [], image_focus: null, map_coords: null, itineraire_image: '',
    itinerary: [], hotels: [], important_notes: [],
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const response = await axios.get(`${API}/admin/me`, { auth: { username, password }, headers: secuHeaders('login') });
      if (response.status === 200) {
        const userRole = response.data?.role || 'full';
        const tabs = response.data?.allowed_tabs || [];
        setRole(userRole);
        setAllowedTabs(tabs);
        setIsAuthenticated(true);
        localStorage.setItem('admin_auth', btoa(`${username}:${password}`));
        localStorage.setItem('admin_role', userRole);
        localStorage.setItem('admin_allowed_tabs', JSON.stringify(tabs));
        // Tab initial selon rôle (accompagnateur excursions-only → direct sur Excursions)
        const soloTab = userRole === 'limited' && tabs.length === 1 && ['excursions', 'embarquement'].includes(tabs[0]) ? tabs[0] : null;
        const urlTab = new URLSearchParams(window.location.search).get('tab');
        setActiveTab(soloTab || (userRole !== 'limited' && urlTab) || (userRole === 'limited' ? 'registrations' : 'trips'));
        fetchTrips();
        // Iter 96i - charger les compteurs
        fetchCounters({ username, password });
      }
    } catch (err) {
      // Iter 587 — IP bloquée après trop de mots de passe faux (429) : on affiche le message du serveur
      setError(err.response?.status === 429 ? `⛔ ${err.response.data?.detail || 'Trop de tentatives, réessaie plus tard.'}` : 'Identifiants incorrects');
    }
  };

  // Iter 457 (Kamel) — session restaurée : on RE-VÉRIFIE rôle + onglets côté serveur
  // (les droits modifiés par le président s'appliquent sans se reconnecter ; localStorage ne fait pas foi).
  useEffect(() => {
    if (!_restored || !isAuthenticated) return;
    axios.get(`${API}/admin/me`, { auth: { username: _restored.username, password: _restored.password }, headers: secuHeaders('restore') })
      .then(r => {
        const userRole = r.data?.role || 'limited';
        const tabs = r.data?.allowed_tabs || [];
        setRole(userRole);
        setAllowedTabs(tabs);
        localStorage.setItem('admin_role', userRole);
        localStorage.setItem('admin_allowed_tabs', JSON.stringify(tabs));
        if (userRole === 'limited' && tabs.length === 1 && ['excursions', 'embarquement'].includes(tabs[0])) setActiveTab(tabs[0]);
      })
      .catch(() => { /* identifiants invalides → les appels API échoueront et renverront à la connexion */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Iter 273 (Kamel) — 🔗 Connexion automatique via lien magique {ADMIN_PATH}?acces=TOKEN
  useEffect(() => {
    if (isAuthenticated) return;
    const t = new URLSearchParams(window.location.search).get('acces');
    if (!t) return;
    (async () => {
      try {
        const magicPw = `magic:${t}`;
        const r = await axios.get(`${API}/admin/me`, { auth: { username: 'lien', password: magicPw }, headers: secuHeaders('magic') });
        const realUser = r.data?.username || 'lien';
        const userRole = r.data?.role || 'limited';
        const tabs = r.data?.allowed_tabs || [];
        setUsername(realUser);
        setPassword(magicPw);
        setRole(userRole);
        setAllowedTabs(tabs);
        setIsAuthenticated(true);
        localStorage.setItem('admin_auth', btoa(`${realUser}:${magicPw}`));
        localStorage.setItem('admin_role', userRole);
        localStorage.setItem('admin_allowed_tabs', JSON.stringify(tabs));
        const soloTab = userRole === 'limited' && tabs.length === 1 && ['excursions', 'embarquement'].includes(tabs[0]) ? tabs[0] : null;
        setActiveTab(soloTab || (userRole === 'limited' ? 'registrations' : 'trips'));
        window.history.replaceState({}, '', ADMIN_PATH);
        fetchTrips();
        fetchCounters({ username: realUser, password: magicPw });
      } catch {
        setError("Lien d'accès invalide ou révoqué — demande un nouveau lien au président.");
        window.history.replaceState({}, '', ADMIN_PATH);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // timeout 45s : évite qu'une requête reste bloquée indéfiniment quand le
  // serveur redémarre après un déploiement (on affiche alors un message clair).
  const getAuthConfig = useCallback(() => ({ auth: { username, password }, timeout: 45000 }), [username, password]);

  // Iter 96i - Charger les compteurs d'actions en attente (badge inscriptions)
  const fetchCounters = useCallback(async (creds) => {
    try {
      const auth = creds || { username, password };
      const r = await axios.get(`${API}/admin/counters`, { auth });
      setCounters(r.data || { overdue_payment: 0, missing_id: 0, missing_room: 0, total_pending: 0 });
      // Iter 157i — compteur quota emails Resend du jour
      try {
        const e = await axios.get(`${API}/admin/emails/today-count`, { auth });
        setEmailsToday(typeof e.data.count === 'number' ? e.data.count : null);
      } catch (err2) { /* non bloquant */ }
      // Iter 157k — envois de masse programmés
      try {
        const s = await axios.get(`${API}/admin/emails/scheduled`, { auth });
        setScheduledEmails(s.data.scheduled || []);
      } catch (err3) { /* non bloquant */ }
      // Iter 158x — 🛂 saisies voyageurs à contrôler (badge)
      try {
        const pc = await axios.get(`${API}/admin/pieces-identite/a-controler`, { auth });
        setControleCount(pc.data.count || 0);
      } catch (err5) { /* non bloquant */ }
      // Iter 157o — 🚨 soldes urgents (départ < 10 jours)
      try {
        const u = await axios.get(`${API}/admin/alerts/soldes-urgents?days=10`, { auth });
        setUrgentSoldes(u.data);
        if ((u.data.count || 0) > 0 && !sessionStorage.getItem('urgentSoldesDismissed')) {
          setShowUrgentPopup(true);
        }
      } catch (err4) { /* non bloquant */ }
      // Iter 157q — 👻 dernier scan fantômes (nocturne)
      try {
        const f = await axios.get(`${API}/admin/audit/fantomes/latest`, { auth });
        setFantomesAlert(f.data);
      } catch (err5) { /* non bloquant */ }
    } catch {
      setCounters({ overdue_payment: 0, missing_id: 0, missing_room: 0, total_pending: 0 });
    }
  }, [username, password]);

  // Au premier chargement (session restauree), recharger les compteurs
  useEffect(() => {
    if (isAuthenticated && username && password) {
      fetchCounters();
      // Iter 97 - Charger aussi les voyages au montage initial (session restauree).
      // Sans ca, l'onglet "Gestion des voyages" reste vide jusqu'a une action explicite.
      fetchTrips();
      // Refresh toutes les 2 minutes
      const id = setInterval(fetchCounters, 120000);
      return () => clearInterval(id);
    }
  }, [isAuthenticated, username, password, fetchCounters]);

  const fetchTrips = async () => {
    setLoadingTrips(true);
    try {
      const response = await axios.get(`${API}/trips?status=all`);
      setTrips(response.data);
    } catch (error) {
      console.error('Erreur:', error);
    }
    setLoadingTrips(false);
    // Iter 235 (Kamel) — Compteur de vues par voyage.
    try {
      const v = await axios.get(`${API}/admin/trips/vues`, getAuthConfig());
      setTripsVues(v.data || {});
    } catch { /* silencieux */ }
  };

  // Iter 235 (Kamel) — Annonce manuelle d'un voyage à la liste de diffusion (1 seule fois).
  const annoncerTrip = async (trip) => {
    try {
      const d = (await axios.post(`${API}/admin/trips/${trip.id}/annoncer?dry_run=true`, {}, getAuthConfig())).data;
      if (d.deja_annonce) { alert('📣 Ce voyage a déjà été annoncé (liste de diffusion + adhérents).'); return; }
      if (d.destinataires === 0) { alert('Aucun destinataire (liste de diffusion + adhérents).'); return; }
      if (!window.confirm(`📣 Envoyer l'annonce « ${trip.title} » par email à ${d.destinataires} personnes = liste de diffusion + tous les adhérents (désinscrits exclus) ?\n\n⚠️ Un seul envoi possible par voyage — irréversible.\n(Envoi progressif ~1 email/seconde, quota du jour vérifié.)`)) return;
      await axios.post(`${API}/admin/trips/${trip.id}/annoncer?dry_run=false`, {}, getAuthConfig());
      alert(`✅ Annonce lancée vers ${d.destinataires} destinataire(s).`);
      fetchTrips();
    } catch (e) { alert('Erreur : ' + (e?.response?.data?.detail || e.message)); }
  };

  const fetchNews = useCallback(async () => {
    setLoadingNews(true);
    try {
      const response = await axios.get(`${API}/admin/news/all`, getAuthConfig());
      setNews(response.data);
    } catch (error) {
      console.error(error);
    }
    setLoadingNews(false);
  }, [getAuthConfig]);

  // Iter 75quater : brouillon par voyage (UX) — évite les PATCH immédiats + reset scroll
  const [tripDrafts, setTripDrafts] = useState({}); // { tripId: { champ: valeur, ... } }
  const [savingTripId, setSavingTripId] = useState(null);
  // Iter 156l (Kamel — onglet Site internet > Voyages) :
  // Quand la saison avance, beaucoup de voyages "Terminés" (`status === 'completed'`)
  // ou dont la `end_date` est dépassée encombrent la liste de gestion des places
  // publiques. Par défaut on les masque + sélecteur déroulant pour y accéder
  // d'un clic si Kamel veut éditer un voyage récurrent pour l'année suivante.
  const [showPastSiteTrips, setShowPastSiteTrips] = useState(false);
  const [tripsYearFilter, setTripsYearFilter] = useState('all');
  // Iter 156m (Kamel) — Modal Cloner un voyage (Turin/Lloret/etc. récurrents)
  const [assistantSource, setAssistantSource] = useState(null);
  const [showProgImport, setShowProgImport] = useState(false);
  const [showAssistantVoyage, setShowAssistantVoyage] = useState(false);
  const [flightInfoTrip, setFlightInfoTrip] = useState(null); // Iter 331 — ✈️ éditeur vols & RDV
  const fmtIsoFr = (iso) => iso ? iso.slice(0, 10).split('-').reverse().join('/') : '';
  const isPastSiteTrip = (trip) => {
    if (!trip) return false;
    if (trip.status === 'completed') return true;
    const endIso = trip.end_date || trip.start_date || '';
    if (!endIso) return false;
    try {
      return new Date(endIso + 'T23:59:59') < new Date();
    } catch { return false; }
  };
  // Iter 324 (Kamel) — Garde-fou : voyage « Terminé » avec une date encore future
  // = invisible du site public alors qu'il devrait probablement être actif (piège Turin prod).
  const isCompletedButFuture = (trip) => {
    if (!trip || trip.status !== 'completed') return false;
    const endIso = trip.end_date || trip.start_date || '';
    if (!endIso) return false;
    try { return new Date(endIso + 'T23:59:59') >= new Date(); } catch { return false; }
  };

  const getEffective = useCallback((trip, field) => {
    const draft = tripDrafts[trip.id];
    if (draft && Object.prototype.hasOwnProperty.call(draft, field)) return draft[field];
    return trip[field];
  }, [tripDrafts]);

  const setDraft = useCallback((tripId, patch) => {
    setTripDrafts(prev => ({ ...prev, [tripId]: { ...(prev[tripId] || {}), ...patch } }));
  }, []);

  const isTripDirty = (trip) => !!tripDrafts[trip.id] && Object.keys(tripDrafts[trip.id]).length > 0;

  const resetDraft = (tripId) => setTripDrafts(prev => {
    const next = { ...prev };
    delete next[tripId];
    return next;
  });

  const saveTripDraft = async (trip) => {
    const draft = tripDrafts[trip.id];
    if (!draft || Object.keys(draft).length === 0) return;
    setSavingTripId(trip.id);
    try {
      // Le champ `weekly_availability` a une route dédiée, le séparer
      const { weekly_availability, ...rest } = draft;
      if (Object.keys(rest).length > 0) {
        await axios.patch(`${API}/admin/trips/${trip.id}`, rest, getAuthConfig());
      }
      if (weekly_availability !== undefined) {
        await axios.patch(`${API}/admin/trips/${trip.id}/weekly`, { weekly_availability }, getAuthConfig());
      }
      // Mise à jour locale de `trips` sans refetch complet (conserve le scroll)
      setTrips(prev => prev.map(t => t.id === trip.id ? { ...t, ...draft } : t));
      resetDraft(trip.id);
    } catch (e) {
      alert('Erreur : ' + (e.response?.data?.detail || e.message));
    }
    setSavingTripId(null);
  };

  const updateTripSpots = async (tripId, newSpots) => {
    const parsed = parseInt(newSpots);
    try {
      await axios.patch(`${API}/admin/trips/${tripId}`, { available_spots: parsed }, getAuthConfig());
      setTrips(prev => prev.map(t => t.id === tripId ? { ...t, available_spots: parsed } : t));
    } catch (error) {
      alert('Erreur lors de la mise à jour');
    }
  };

  const updateWeeklySpots = async (tripId, weekKey, value) => {
    const trip = trips.find(t => t.id === tripId);
    if (!trip) return;
    const newSpots = parseInt(value);
    const weekly = { ...(trip.weekly_availability || {}), [weekKey]: newSpots };
    try {
      await axios.patch(`${API}/admin/trips/${tripId}/weekly`, { weekly_availability: weekly }, getAuthConfig());
      setTrips(prev => prev.map(t => t.id === tripId ? { ...t, weekly_availability: weekly } : t));
    } catch (error) {
      alert('Erreur lors de la mise à jour');
    }
  };

  const saveTrip = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...tripForm };
      // Convert text areas to arrays
      if (payload.included_text !== undefined) {
        payload.included = payload.included_text.split('\n').map(s => s.trim()).filter(Boolean);
        delete payload.included_text;
      }
      if (payload.not_included_text !== undefined) {
        payload.not_included = payload.not_included_text.split('\n').map(s => s.trim()).filter(Boolean);
        delete payload.not_included_text;
      }
      if (payload.highlights_text !== undefined) {
        payload.highlights = payload.highlights_text.split('\n').map(s => s.trim()).filter(Boolean);
        delete payload.highlights_text;
      }
      payload.gallery_images = (payload.gallery_images || []).map(s => String(s).trim()).filter(Boolean);
      payload.map_coords = (payload.map_coords && Number.isFinite(+payload.map_coords.lat) && Number.isFinite(+payload.map_coords.lng) && payload.map_coords.lat !== '' && payload.map_coords.lng !== '') ? { lat: +payload.map_coords.lat, lng: +payload.map_coords.lng } : null;
      payload.itineraire_image = String(payload.itineraire_image || '').trim();
      payload.itinerary = (payload.itinerary || []).map((d, i) => {
        const o = { ...d, day: i + 1, events: (d.events || d.activities || []).map(x => String(x).trim()).filter(Boolean) };
        delete o.activities;
        return o;
      });
      payload.hotels = (payload.hotels || []).filter(h => (h.name || '').trim());
      payload.important_notes = (payload.important_notes || []).map(x => String(x).trim()).filter(Boolean);
      payload.excursions_image_url = String(payload.excursions_image_url || '').trim();
      payload.excursions = (payload.excursions || []).map(e => ({ jour: String(e.jour || '').trim(), nom: String(e.nom || '').trim(), type: String(e.type || '').trim(), prix_adulte: String(e.prix_adulte || '').trim(), prix_enfant: String(e.prix_enfant || '').trim() })).filter(e => e.nom);
      if (payload.multi_dates) delete payload.total_spots; else delete payload.capacite_par_date; // Iter 943 — page multidates : places PAR DATE
      delete payload.multi_dates;
      delete payload.available_spots; // Iter 569 — le plafond suit la capacité côté serveur
      // Iter 761 — blocs « 1 ligne = 1 élément »
      if (payload.reductions_text !== undefined) { payload.reductions = linesToArr(payload.reductions_text); delete payload.reductions_text; }
      if (payload.departure_schedule_text !== undefined) { payload.departure_schedule = textToDep(payload.departure_schedule_text); delete payload.departure_schedule_text; }
      if (payload.transport_option && payload.transport_departures_text !== undefined) payload.transport_option = { ...payload.transport_option, departures: textToDep(payload.transport_departures_text) };
      delete payload.transport_departures_text;
      if (payload.has_car_schedule) payload.car_schedule = { aller: textToDep(payload.car_aller_text), retour: textToDep(payload.car_retour_text), note: String(payload.car_schedule_note || '').trim() };
      delete payload.has_car_schedule; delete payload.car_aller_text; delete payload.car_retour_text; delete payload.car_schedule_note;
      if (payload.activity_suggestions_text !== undefined) { payload.activity_suggestions = textToAct(payload.activity_suggestions_text); delete payload.activity_suggestions_text; }
      if (payload.room_pricing_text !== undefined) {
        payload.room_pricing = linesToArr(payload.room_pricing_text).map(l => { const [type = '', price = '', base = ''] = l.split('|').map(x => x.trim()); return { type, price: parseFloat(String(price).replace(',', '.')) || 0, base: /base/i.test(base) }; });
        delete payload.room_pricing_text;
      }
      if (payload.paid_options_text !== undefined) { payload.paid_options = textToAct(payload.paid_options_text); delete payload.paid_options_text; }
      // Iter 772 — spa_flash est un TEXTE côté serveur (phrase spa) : la case cochée garde la phrase existante ou en met une par défaut
      if (payload.spa_flash !== undefined) { payload.spa_flash = payload.spa_flash ? (payload.spa_flash_texte || 'Spa & bien-être') : null; }
      delete payload.spa_flash_texte;
      if (payload.desert_activities_text !== undefined) { payload.desert_activities = linesToArr(payload.desert_activities_text); delete payload.desert_activities_text; }
      if (!payload.transport_option) delete payload.transport_option; // Iter 760 — on ne touche au bloc Transport que s'il existe
      if (editingTrip) {
        await axios.put(`${API}/admin/trips/${editingTrip}`, payload, getAuthConfig());
        alert('Voyage mis à jour');
      } else {
        await axios.post(`${API}/admin/trips`, payload, getAuthConfig());
        alert('Voyage créé');
      }
      setShowTripForm(false);
      setEditingTrip(null);
      setTripForm({ title: '', destination: '', description: '', transport: 'autocar', start_date: '', end_date: '', price_from: 0, price_info: '', available_spots: 0, total_spots: 0, image_url: '', program_pdf: '', program_details: '', highlights_text: '', included_text: '', not_included_text: '', gallery_images: [], image_focus: null, map_coords: null, itineraire_image: '', itinerary: [], hotels: [], important_notes: [] });
      fetchTrips();
    } catch (error) {
      alert('Erreur lors de la sauvegarde');
    }
  };

  const deleteTrip = async (tripId, title) => {
    if (!window.confirm(`Supprimer le voyage "${title}" ? Cette action est irréversible.`)) return;
    try {
      await axios.delete(`${API}/admin/trips/${tripId}`, getAuthConfig());
      fetchTrips();
      alert('Voyage supprimé');
    } catch (error) {
      alert('Erreur lors de la suppression');
    }
  };

  const editTrip = (trip) => {
    setEditingTrip(trip.id);
    setTripForm({
      title: trip.title || '', destination: trip.destination || '', description: trip.description || '',
      transport: trip.transport || 'autocar', start_date: trip.start_date || '', end_date: trip.end_date || '',
      price_from: trip.price_from || 0, price_info: trip.price_info || '',
      available_spots: trip.available_spots || 0, total_spots: trip.total_spots || 0,
      multi_dates: !!(trip.multi_dates && (trip.date_options || []).length > 1),
      capacite_par_date: trip.capacite_defaut || ((trip.date_options || []).length > 1 ? Math.round((trip.total_spots || 0) / trip.date_options.length) : 0),
      image_url: trip.image_url || '', program_pdf: trip.program_pdf || '',
      program_details: trip.program_details || '',
      highlights_text: (trip.highlights || []).join('\n'),
      included_text: (trip.included || []).join('\n'),
      not_included_text: (trip.not_included || []).join('\n'),
      chambres_infos: trip.chambres_infos || '',
      chambres_infos_titre: trip.chambres_infos_titre || '',
      meal_plan: trip.meal_plan || '', meal_plan_label: trip.meal_plan_label || '',
      tarif_note: trip.tarif_note || '', precisions_hebergement: trip.precisions_hebergement || '',
      transport_option: trip.transport_option || null,
      reductions_text: (trip.reductions || []).join('\n'),
      departure_schedule_text: depToText(trip.departure_schedule),
      transport_departures_text: depToText(trip.transport_option?.departures),
      car_aller_text: depToText(trip.car_schedule?.aller), car_retour_text: depToText(trip.car_schedule?.retour), car_schedule_note: trip.car_schedule?.note || '',
      has_car_schedule: !!trip.car_schedule,
      activity_suggestions_text: actToText(trip.activity_suggestions),
      desert_activities_text: (trip.desert_activities || []).join('\n'),
      spa_flash: !!trip.spa_flash,
      spa_flash_texte: typeof trip.spa_flash === 'string' ? trip.spa_flash : '',
      vue_piscine_texte: trip.vue_piscine_texte || '',
      room_pricing_text: (trip.room_pricing || []).map(r => [r.type || '', r.price ?? '', r.base ? 'base' : ''].join(' | ').replace(/ \| $/, '')).join('\n'),
      paid_options_text: actToText(trip.paid_options),
      gallery_images: trip.gallery_images || [], image_focus: trip.image_focus || null, map_coords: trip.map_coords || null, itineraire_image: trip.itineraire_image || '',
      itinerary: trip.itinerary || [], hotels: trip.hotels || [], important_notes: trip.important_notes || [],
      excursions: trip.excursions || [], excursions_image_url: trip.excursions_image_url || '',
    });
    setShowTripForm(true);
    setTimeout(() => document.querySelector('[data-testid="trip-form"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  // Ouvre la fiche d'un voyage par id (après création via l'assistant)
  const editTripById = async (tripId) => {
    try {
      const r = await axios.get(`${API}/trips/${tripId}`);
      editTrip(r.data);
    } catch (e) { alert('Voyage introuvable'); }
  };
  // Iter 663 — ?tab=trips&edit=<id> (ouverture depuis une avant-première) : ouvre directement la fiche voyage en modification
  useEffect(() => {
    const editId = new URLSearchParams(window.location.search).get('edit');
    if (isAuthenticated && editId && activeTab === 'trips') {
      editTripById(editId);
      window.history.replaceState({}, '', `${window.location.pathname}?tab=trips`);
    }
  }, [isAuthenticated, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePublishTrip = async (trip) => {
    const hidden = !!trip.is_hidden_public;
    if (!hidden && !window.confirm(`Masquer « ${trip.title} » du site et du formulaire d'inscription ?`)) return;
    try {
      await axios.post(`${API}/admin/trips/${trip.id}/${hidden ? 'restore-public' : 'delete-public'}`, {}, getAuthConfig());
      await fetchTrips();
      if (hidden) alert(`✅ « ${trip.title} » est maintenant visible sur le site et dans le formulaire d'inscription.`);
    } catch (e) { alert('Erreur : ' + (e.response?.data?.detail || e.message)); }
  };

  const createNews = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/news`, newsForm, getAuthConfig());
      setShowNewsForm(false);
      setNewsForm({ title: '', content: '', category: 'info', published: true });
      fetchNews();
      alert('Actualité créée');
    } catch (error) {
      alert('Erreur lors de la création');
    }
  };

  const deleteNews = async (newsId) => {
    if (!window.confirm('Supprimer cette actualité ?')) return;
    try {
      await axios.delete(`${API}/admin/news/${newsId}`, getAuthConfig());
      fetchNews();
    } catch (error) {
      alert('Erreur lors de la suppression');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setUsername('');
    setPassword('');
    setRole('full');
    localStorage.removeItem('admin_auth');
    localStorage.removeItem('admin_role');
    localStorage.removeItem('admin_allowed_tabs');
    localStorage.removeItem('admin_active_tab');
  };

  useEffect(() => {
    if (isAuthenticated && activeTab === 'news') fetchNews();
  }, [activeTab, isAuthenticated, fetchNews]);

  // Iter 94 — Scroll en haut au montage de l'admin et lors d'un changement d'onglet
  // (corrige le bug où la page apparait au milieu/bas, perdant l'utilisateur)
  useEffect(() => {
    if (isAuthenticated) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, [isAuthenticated, activeTab]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-r from-[#79c9ea] to-[#5ba8c8] flex items-center justify-center p-4">
        <div className="card max-w-md w-full p-8">
          <h1 className="text-3xl font-bold text-center mb-6 text-[#1E3A5F]" data-testid="admin-login-title">Administration</h1>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nom d'utilisateur</label>
              <input type="text" value={username} onChange={(e) => setUsername(e.target.value)}
                autoCapitalize="none" autoCorrect="off" spellCheck={false} autoComplete="username"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#79c9ea] focus:border-transparent"
                data-testid="admin-username" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Mot de passe</label>
              <div className="relative">
                <input type={showLoginPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2 pr-11 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#79c9ea] focus:border-transparent"
                  data-testid="admin-password" required />
                <button type="button" onClick={() => setShowLoginPassword(v => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-500 hover:text-[#1E3A5F] transition"
                  title={showLoginPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  data-testid="admin-password-toggle">
                  {showLoginPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/></svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                  )}
                </button>
              </div>
            </div>
            {error && <p className="text-red-500 text-sm" data-testid="admin-error">{error}</p>}
            <button type="submit" className="w-full bg-[#1E3A5F] text-white py-3 rounded-lg font-bold hover:bg-[#2a4f7a] transition" data-testid="admin-login-btn">
              Se connecter
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <AdminErrorBoundary>
    <div className="admin-dashboard min-h-screen bg-gray-50">
      <BandeauHorsLigne />
      {/* Iter 149d — Bannière bienvenue Messa (validée par Kamel)
          Affichée 1 fois, fermable, mémorisée localement (localStorage). */}
      <WelcomeMessaBanner role={role} />
      {/* Iter 517 — bandeau tâches en retard / du jour (cliquable → onglet Tâches) */}
      {role !== 'limited' && <TachesBanner getAuthConfig={getAuthConfig} onOpen={() => setActiveTab('taches')} active={activeTab === 'taches'} />}
      <div className="bg-white shadow-md">
        <div className="w-full max-w-[1800px] mx-auto px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between gap-2">
          <h1 className="text-base sm:text-2xl font-bold text-[#1E3A5F] whitespace-nowrap" data-testid="admin-dashboard-title">
            <span className="hidden sm:inline">Tableau de bord </span>{role === 'limited' ? 'Bénévole' : 'Admin'}
            <span className="ml-2 text-xs font-normal text-gray-500" data-testid="admin-role-badge">
              ({username}{role === 'limited' ? ' — accès limité' : ''})
            </span>
          </h1>
          <div className="flex items-center gap-1.5 sm:gap-3 flex-wrap justify-end">
            {/* Iter 157i — Compteur quota emails Resend du jour (masqué sur mobile, sauf alerte) */}
            {emailsToday !== null && (
              <span
                className={`text-xs font-bold px-2.5 py-1 rounded-full border ${emailsToday < 1000 ? 'hidden sm:inline-block ' : ''}${emailsToday >= 2000 ? 'bg-red-100 text-red-700 border-red-300' : emailsToday >= 1000 ? 'bg-amber-100 text-amber-700 border-amber-300' : 'bg-emerald-50 text-emerald-700 border-emerald-300'}`}
                title="Emails envoyés aujourd'hui (forfait Resend payant : garde-fou 2000/jour pour rester sous 50 000/mois, remise à zéro à minuit UTC)"
                data-testid="emails-today-badge"
              >
                ✉️ {emailsToday}/2000 aujourd'hui
              </span>
            )}
            {/* Iter 161 — 💶 badge signalements « j'ai déjà payé » */}
            <DejaPayeAlerts API={API} username={username} password={password} />
            {/* Iter 157q — 👻 badge fantômes (scan nocturne) : visible seulement si anomalies */}
            {fantomesAlert && fantomesAlert.total_issues > 0 && (
              <button
                onClick={() => { setControleSub('financier'); setActiveTab('controle'); }}
                className="text-xs font-bold px-2.5 py-1 rounded-full border bg-purple-50 text-purple-700 border-purple-300 hover:bg-purple-100"
                title={`Le scan automatique a détecté ${fantomesAlert.total_issues} fantôme(s) (sièges de bus orphelins) — cliquez pour voir le détail dans l'Audit`}
                data-testid="fantomes-alert-badge"
              >
                👻 {fantomesAlert.total_issues} fantôme{fantomesAlert.total_issues > 1 ? 's' : ''}
              </button>
            )}
            {/* Iter 157o — 🚨 badge soldes urgents (rouvre le pop-up) — comptes complets uniquement */}
            {role === 'full' && urgentSoldes && urgentSoldes.count > 0 && (
              <button
                onClick={() => setShowUrgentPopup(true)}
                className="text-xs font-bold px-2.5 py-1 rounded-full border bg-red-50 text-red-700 border-red-300 hover:bg-red-100 animate-pulse"
                title={`${urgentSoldes.count} fiche(s) non soldée(s) partent dans moins de 10 jours — ${urgentSoldes.total_reste} € à encaisser`}
                data-testid="urgent-soldes-badge"
              >
                🚨 {urgentSoldes.count} à solder &lt;10j
              </button>
            )}
            {/* Iter 157k — envois de masse programmés (demain 8h) */}
            {scheduledEmails.length > 0 && (
              <span className="relative">
                <button
                  onClick={() => setShowScheduled((v) => !v)}
                  className="text-xs font-bold px-2.5 py-1 rounded-full border bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100"
                  title="Envois de masse programmés — cliquez pour voir/annuler"
                  data-testid="scheduled-emails-badge"
                >
                  ⏰ {scheduledEmails.length} programmé{scheduledEmails.length > 1 ? 's' : ''}
                </button>
                {showScheduled && (
                  <div className="absolute right-0 top-8 z-50 bg-white border rounded-lg shadow-xl p-3 w-80 text-left" data-testid="scheduled-emails-panel">
                    <div className="text-xs font-bold text-gray-700 mb-2">⏰ Envois programmés</div>
                    {scheduledEmails.map((s) => (
                      <div key={s.id} className="flex items-start justify-between gap-2 border-t py-2 text-[11px]">
                        <div>
                          <div className="font-bold text-gray-800">{s.label || s.subject}</div>
                          <div className="text-gray-500">📅 {new Date(s.scheduled_for).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })} — « {s.subject} »</div>
                        </div>
                        <button onClick={() => cancelScheduled(s.id)}
                          className="text-red-500 hover:text-red-700 font-bold shrink-0"
                          data-testid={`scheduled-email-cancel-${s.id}`}>✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </span>
            )}
            <NouveautesBtn getAuthConfig={getAuthConfig} />
            <button onClick={handleLogout} className="text-sm text-gray-500 hover:text-red-500 transition" data-testid="admin-logout">Déconnexion</button>
          </div>
        </div>
      </div>

      {/* Iter 157o (Kamel) — 🚨 POP-UP URGENT : fiches non soldées à moins de 10 jours du départ */}
      {showUrgentPopup && role === 'full' && urgentSoldes && urgentSoldes.count > 0 && (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" data-testid="urgent-soldes-popup">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col overflow-hidden border-4 border-red-500">
            <div className="bg-red-600 text-white px-5 py-4 flex items-center justify-between">
              <div>
                <div className="font-bold text-lg">🚨 Urgent — soldes à encaisser avant le départ</div>
                <div className="text-sm opacity-90">{urgentSoldes.count} fiche{urgentSoldes.count > 1 ? 's' : ''} non soldée{urgentSoldes.count > 1 ? 's' : ''} part{urgentSoldes.count > 1 ? 'ent' : ''} dans moins de 10 jours — <strong>{urgentSoldes.total_reste.toFixed(2)} €</strong> à encaisser</div>
              </div>
              <button onClick={() => { setShowUrgentPopup(false); sessionStorage.setItem('urgentSoldesDismissed', '1'); }}
                className="text-white/80 hover:text-white text-2xl font-bold px-2" data-testid="urgent-soldes-close">✕</button>
            </div>
            <div className="overflow-y-auto p-4 space-y-2">
              {urgentSoldes.items.map((it) => (
                <div key={it.reg_id} className="border border-red-200 bg-red-50 rounded-lg px-4 py-2.5 flex items-center justify-between gap-3" data-testid={`urgent-solde-row-${it.reg_id}`}>
                  <div className="min-w-0">
                    <div className="font-bold text-gray-900 text-sm">{it.nom} {it.prenom} <span className="font-normal text-gray-500">— {it.voyage}</span></div>
                    <div className="text-xs text-gray-600">
                      🚌 Départ le {new Date(it.date_depart).toLocaleDateString('fr-FR')} (<strong className="text-red-700">J-{it.jours_avant_depart}</strong>)
                      {it.telephone && <> · 📞 {it.telephone}</>}
                      · payé {it.paye.toFixed(0)} € / {it.total.toFixed(0)} €
                    </div>
                  </div>
                  <div className="text-right shrink-0 flex flex-col items-end gap-1">
                    <div className="text-red-700 font-bold text-base">{it.reste.toFixed(2)} €</div>
                    <div className="text-[10px] text-red-500 font-semibold">RESTE À PAYER</div>
                    {it.telephone && (() => {
                      // Iter 157p — bouton relance WhatsApp direct depuis le pop-up
                      const digits = it.telephone.replace(/\D/g, '');
                      const intl = digits.startsWith('0') ? '33' + digits.slice(1) : digits;
                      const msg = `Bonjour ${it.prenom || ''} 👋\n\nVotre départ pour ${it.voyage} approche (le ${new Date(it.date_depart).toLocaleDateString('fr-FR')}).\nSelon notre facture, il reste ${it.reste.toFixed(2)} € à régler pour finaliser votre dossier.\n\nVous pouvez régler en ligne : https://rhoneamitievoyages.fr/payer\nou nous contacter pour un rendez-vous.\n\nMerci et à très vite !\nRhône Alpes Amitié Voyages`;
                      return (
                        <a href={`https://wa.me/${intl}?text=${encodeURIComponent(msg)}`}
                          target="_blank" rel="noopener noreferrer"
                          className="text-[11px] px-2.5 py-1 bg-[#25D366] hover:bg-[#1DA851] text-white rounded-full font-bold"
                          data-testid={`urgent-solde-whatsapp-${it.reg_id}`}>
                          📞 Relancer WhatsApp
                        </a>
                      );
                    })()}
                  </div>
                </div>
              ))}
            </div>
            <div className="px-5 py-3 bg-gray-50 border-t flex items-center justify-between">
              <p className="text-xs text-gray-500">💡 Si un règlement vient d'arriver, saisis-le : la fiche disparaîtra de cette liste.</p>
              <button onClick={() => { setShowUrgentPopup(false); sessionStorage.setItem('urgentSoldesDismissed', '1'); }}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-bold shrink-0"
                data-testid="urgent-soldes-ok">J'ai vu, je m'en occupe</button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border-b" data-testid="admin-nav-bar">
        <div className="w-full max-w-[1800px] mx-auto px-4">
          {(() => {
            // Iter 96h - Reorganisation admin en 4 megacategories
            // Chaque tab plat est mappe a une megacategorie
            // Iter 156 (Kamel — Grand Ménage Admin) : cheques_attente + remises
            // déplacés dans Finances (logique financière).
            // Iter 158k (Kamel) : Statistiques déplacé Finances → Inscriptions ;
            // onglet « Échéancier à suivre » supprimé de la nav (doublon — la vue
            // échéancier reste accessible dans Statistiques, ou via ?tab=echeancier).
            const TAB_TO_CATEGORY = {
              registrations: 'inscriptions',
              gestion: 'inscriptions',
              stats: 'inscriptions',
              imports: 'inscriptions',
              permanences: 'inscriptions',
              interactions: 'inscriptions',
              urgent_reminders: 'inscriptions',
              excursions: 'inscriptions',
              sondages: 'inscriptions',
              destination_notes: 'inscriptions',
              espace: 'inscriptions',
              reponses: 'inscriptions',
              prospection: 'prospection',
              marketing: 'prospection',
              bus_plans: 'inscriptions',
              espagne: 'inscriptions',
              form_textes: 'site',
              embarquement: 'inscriptions',
              brouillons: 'inscriptions',
              tel_partages: 'inscriptions',
              google_contacts: 'admin',
              cheques_attente: 'finances',
              remises: 'finances',
              pointage_banque: 'finances',
              sante_fiscale: 'finances',
              moyens_paiement: 'finances',
              prelevements: 'finances',
              annulations: 'finances',
              depenses: 'finances',
              import_recettes: 'finances',
              rattrapage_express: 'finances',
              assistant_ia: 'site',
              caisse: 'finances',
              ancv: 'finances',
              association: 'finances',
              bilan: 'finances',
              tresorerie: 'finances',
              suivi_tva: 'finances',
              audit_compta: 'finances',
              audit: 'admin',
              controle: 'admin',
              accueil: 'site',
              communication: 'site',
              trips: 'site',
              feuille_route: 'site',
              catalogue: 'site',
              corbeille: 'site',
              news: 'site',
              testimonials: 'site',
              visuels_ia: 'site',
              messages: 'site',
              staff: 'admin',
              access: 'admin',
              sante: 'admin',
              coherence: 'admin',
              taches: 'admin',
              coffre_fort: 'admin',
            };
            // Iter 155 (Kamel — perf admin) — Outils rarement utilisés MASQUÉS
            // de la nav mais le code reste lazy-loadé : accessibles via URL
            // ?tab=imports / ?tab=audit / ?tab=dino / ?tab=sante si besoin.
            const CATEGORIES_BASE = [
              { id: 'inscriptions', label: 'Inscriptions', icon: '🏢', tabs: ['registrations', 'gestion', 'espace', 'excursions', 'stats', 'permanences', 'urgent_reminders', 'bus_plans', 'espagne', 'embarquement', 'tel_partages', 'brouillons'] },
              { id: 'prospection', label: 'Prospection', icon: '📢', tabs: ['prospection', 'marketing'] },
              { id: 'finances', label: 'Finances', icon: '💶', tabs: ['sante_fiscale', 'moyens_paiement', 'cheques_attente', 'remises', 'pointage_banque', 'prelevements', 'depenses', 'import_recettes', 'rattrapage_express', 'caisse', 'ancv', 'association', 'annulations', 'bilan', 'tresorerie', 'suivi_tva'] },
              { id: 'site', label: 'Site internet', icon: '🌐', tabs: ['trips', 'accueil', 'communication', 'feuille_route', 'corbeille', 'news', 'testimonials', 'visuels_ia', 'messages', 'form_textes', 'assistant_ia'] },
              { id: 'admin', label: 'Admin', icon: '⚙️', tabs: ['taches', 'controle', 'staff', 'access', 'coffre_fort', 'google_contacts'] },
            ];
            // Tabs visibles par defaut pour limited (sans permissions custom)
            const DEFAULT_LIMITED_TABS = ['registrations'];
            // Iter 96i - Calcule les onglets autorises pour ce role
            let visibleTabs;
            if (role === 'full') {
              visibleTabs = null;  // null = tous
            } else if (allowedTabs && allowedTabs.length > 0) {
              // Iter 211 — Profil accompagnateur : si SEUL 'excursions' est coché,
              // le compte n'a accès QU'À cet onglet (pas d'Inscriptions de base).
              if (allowedTabs.length === 1 && ['excursions', 'embarquement'].includes(allowedTabs[0])) {
                visibleTabs = new Set([allowedTabs[0]]);
              } else {
                // Permissions personnalisees + tab limited de base
                visibleTabs = new Set([...DEFAULT_LIMITED_TABS, ...allowedTabs.filter(t => t !== 'visuels_ia'), ...(allowedTabs.some(t => ESPACE_SUBS[t]) ? ['espace'] : [])]);
              }
            } else {
              visibleTabs = new Set(DEFAULT_LIMITED_TABS);
            }
            const excursionsOnly = role === 'limited' && allowedTabs?.length === 1 && allowedTabs[0] === 'excursions';
            const embarquementOnly = role === 'limited' && allowedTabs?.length === 1 && allowedTabs[0] === 'embarquement';
            const CATEGORIES = CATEGORIES_BASE
              .map(c => ({
                ...c,
                ...(excursionsOnly && c.id === 'inscriptions' ? { label: 'Extras & excursions', icon: '🎡' } : {}),
                ...(embarquementOnly && c.id === 'inscriptions' ? { label: 'Scan Bus', icon: '🎫' } : {}),
                tabs: visibleTabs ? c.tabs.filter(t => visibleTabs.has(t)) : c.tabs,
              }))
              .filter(c => c.tabs.length > 0);
            const TAB_LABELS = {
              google_contacts: '🔗 Google Contacts',
              trips: 'Voyages',
              feuille_route: '🧭 Feuille de route',
              catalogue: '🗺 Catalogue voyages',
              corbeille: '🗑 Corbeille voyages',
              news: 'Actualités',
              accueil: '🏠 Accueil',
              communication: '📣 Communication',
              registrations: 'Inscriptions',
              gestion: "Gestion d'un voyage",
              echeancier: '💰 Échéancier à suivre',
              cheques_attente: '🏦 Chèques en attente',
              remises: '📥 Remises bancaires',
              pointage_banque: '✅ Pointage banque',
              sante_fiscale: '🎯 Santé fiscale',
              moyens_paiement: '💳 Moyens de paiement',
              prelevements: '🔁 Prélèvements',
              annulations: '🧯 Annulations',
              permanences: '📅 Rendez-vous',
              urgent_reminders: '📬 Relances auto',
              coffre_fort: "🗄️ À l'ancienne",
              excursions: '🎡 Extras & excursions',
              espace: '👤 Espace adhérent',
              taches: '✅ Tâches',
              tel_partages: '📱 Numéros & dates',
              bus_plans: '🚌 Plan de bus',
              espagne: '🇪🇸 Espagne 2027',
              form_textes: '✏️ Textes du site',
              embarquement: '🎫 Scan Bus',
              brouillons: '📝 Formulaires abandonnés',
              marketing: '📣 Emails auto',
              prospection: '📢 Prospection',
              depenses: 'Dépenses',
              import_recettes: '📥 Import recettes',
              rattrapage_express: '⚡ Rattrapage 24/25',
              assistant_ia: '🤖 Assistant IA',
              caisse: '💰 Caisse Espèces',
              ancv: '🎟️ Livre ANCV',
              staff: 'Répertoire équipe',
              stats: 'Statistiques',
              imports: 'Imports en masse',
              association: 'Frais généraux',
              bilan: 'Bilan annuel',
              tresorerie: '💰 Récap trésorerie',
              suivi_tva: '🧾 Suivi TVA',
              audit_compta: '🔍 Audit',
              audit: '🔍 Audit financier',
              testimonials: 'Témoignages',
              visuels_ia: '🎨 Visuels IA',
              messages: 'Modèles de messages',
              dino: '🐕 Dino WhatsApp',
              access: 'Accès du site',
              sante: '🩺 Santé du site',
              coherence: '🛡️ Contrôle cohérence',
              controle: '🩺 Centre de contrôle',
            };
            const activeCategoryId = TAB_TO_CATEGORY[activeTab] || CATEGORIES[0]?.id;
            const activeCategory = CATEGORIES.find(c => c.id === activeCategoryId) || CATEGORIES[0] || { id: 'inscriptions', tabs: [] };

            return (
              <>
                {/* Niveau 1 : meta-categories */}
                <div className="flex flex-wrap gap-1 pt-1">
                  {CATEGORIES.map(cat => {
                    const isActive = cat.id === activeCategoryId;
                    // Iter 96i - Badge compteur sur Inscriptions (+ Iter 158x saisies à contrôler)
                    const badgeCount = (cat.id === 'inscriptions' && !excursionsOnly) ? counters.total_pending + controleCount : 0;
                    return (
                      <button
                        key={cat.id}
                        onClick={() => setActiveTab(cat.tabs[0])}
                        data-testid={`admin-megacat-${cat.id}`}
                        className={`relative px-4 sm:px-5 py-3 font-bold text-xs sm:text-sm transition flex items-center gap-2 rounded-t-lg ${
                          isActive
                            ? 'bg-[#1E3A5F] text-white shadow-md'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        <span className="text-base" aria-hidden>{cat.icon}</span>
                        <span>{cat.label}</span>
                        {badgeCount > 0 && cat.label !== 'Scan Bus' && (
                          <span
                            className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] font-bold rounded-full h-5 min-w-[20px] px-1 flex items-center justify-center shadow-md ring-2 ring-white"
                            data-testid={`admin-megacat-${cat.id}-badge`}
                            title={`${counters.overdue_payment} paiement(s) en retard, ${counters.missing_id} pièce(s) ID manquante(s), ${counters.missing_room} chambre(s) à attribuer${controleCount > 0 ? `, ${controleCount} saisie(s) voyageur à contrôler` : ''}`}
                          >
                            {badgeCount > 99 ? '99+' : badgeCount}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {/* Niveau 2 : sous-onglets de la categorie active (masque si une seule entree) */}
                {activeCategory.tabs.length > 1 && (
                <div className="flex flex-wrap items-center gap-0 border-t border-[#1E3A5F]/20 bg-blue-50/30 -mx-4 px-4">
                  {(() => {
                    // Iter 532 (Kamel) — « Quotidien » visible, « Outils avancés » repliés (l'onglet actif reste toujours visible)
                    const avances = activeCategory.tabs.filter(t => !QUOTIDIEN.has(t));
                    const visibles = activeCategory.tabs.filter(t => QUOTIDIEN.has(t) || t === activeTab || showAvances || avances.length === activeCategory.tabs.length);
                    return [...visibles.map(tab => ({ tab })), ...(avances.length && avances.length < activeCategory.tabs.length ? [{ toggle: true, n: avances.length }] : [])];
                  })().map(({ tab, toggle, n }) => toggle ? (
                    <button key="__avances" type="button" onClick={() => setShowAvances(v => !v)} data-testid="admin-tabs-avances-toggle"
                      className="ml-auto px-3 py-2 text-[11px] font-semibold text-gray-500 hover:text-[#1E3A5F] whitespace-nowrap">
                      {showAvances ? '▴ Masquer les outils avancés' : `⚙️ Outils avancés (${n}) ▾`}
                    </button>
                  ) : (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      data-testid={`admin-tab-${tab}`}
                      className={`px-3 sm:px-4 py-2.5 font-semibold text-xs sm:text-sm transition whitespace-nowrap ${
                        activeTab === tab
                          ? 'text-[#1E3A5F] border-b-2 border-[#1E3A5F] bg-white'
                          : 'text-gray-500 hover:text-[#1E3A5F]'
                      }`}
                    >
                      {TAB_LABELS[tab] || tab}
                      {tab === 'registrations' && counters.pending_changes > 0 && (
                        <span
                          className="ml-1.5 bg-red-600 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 align-middle"
                          data-testid="pending-changes-badge"
                          title={`${counters.pending_changes} demande(s) de modification adhérent à valider`}
                        >
                          ✏️ {counters.pending_changes}
                        </span>
                      )}
                      {tab === 'urgent_reminders' && controleCount > 0 && (
                        <span
                          className="ml-1.5 bg-red-600 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 align-middle"
                          data-testid="controle-saisies-badge"
                          title={`${controleCount} saisie(s) voyageur à contrôler`}
                        >
                          🛂 {controleCount}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
                )}
                {TAB_HELP[activeTab] && !helpHidden && (
                  <p className="text-[11px] text-gray-500 bg-white/70 border-t border-[#1E3A5F]/10 -mx-4 px-4 py-1.5 flex items-start gap-2" data-testid="admin-tab-help">
                    <span>❓</span><span className="flex-1">{TAB_HELP[activeTab]}</span>
                    <button type="button" onClick={() => { setHelpHidden(true); try { localStorage.setItem('admin_help_hidden', '1'); } catch {} }} className="text-gray-400 hover:text-gray-600" title="Masquer les rappels" data-testid="admin-tab-help-hide">×</button>
                  </p>
                )}
              </>
            );
          })()}
        </div>
      </div>

      <div className="w-full max-w-[1800px] mx-auto px-4 py-8">
        {/* Iter 481 (Kamel) — bannière « bus presque complet » retirée du dashboard (envahissante ; l'alerte e-mail reste). */}
        {/* Iter 832 (Kamel) — « ✨ Modèle » (Iter 122) retiré : l'Assistant 🧙 crée vitrine + formule + tâches en une fois */}
        {/* Modale suppression voyage (Iter 122c — Kamel) */}
        <TripDeletionModal
          open={!!tripToDelete}
          trip={tripToDelete}
          onClose={() => setTripToDelete(null)}
          getAuthConfig={getAuthConfig}
          onDone={() => fetchTrips()}
        />
        {/* Iter 156m (Kamel) — Modale Cloner un voyage pour l'année suivante */}
        {/* Iter 331 — ✈️ Éditeur Vols & RDV (horaires modifiables par Kamel) */}
        {flightInfoTrip && (
          <Suspense fallback={null}>
            <FlightInfoModal trip={flightInfoTrip} getAuthConfig={getAuthConfig}
              onClose={() => setFlightInfoTrip(null)} onSaved={() => fetchTrips()} />
          </Suspense>
        )}
        <AssistantVoyage
          open={showAssistantVoyage}
          initialSource={assistantSource}
          onClose={() => { setShowAssistantVoyage(false); setAssistantSource(null); }}
          getAuthConfig={getAuthConfig}
          onSuccess={fetchTrips}
          onEditTrip={editTripById}
          onFlightsTrip={async (id) => { try { const r = await axios.get(`${API}/trips/${id}`); setFlightInfoTrip(r.data); } catch (e) { alert('Voyage introuvable'); } }}
          onPublishTrip={async (id) => { try { const r = await axios.get(`${API}/trips/${id}`); await togglePublishTrip(r.data); } catch (e) { alert('Voyage introuvable'); } }}
        />
        {/* ===== VOYAGES ===== */}
        {activeTab === 'trips' && (
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <h2 className="text-base font-bold text-[#1E3A5F]">
                {role === 'limited' ? 'Voyages & places disponibles' : 'Gestion des voyages'}
              </h2>
              {role !== 'limited' && (
                <div className="flex flex-wrap gap-1.5 ml-auto">
                  <button onClick={() => setShowAssistantVoyage(true)}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1 rounded-lg font-bold text-xs transition"
                    data-testid="open-assistant-voyage-btn"
                    title="Assistant pas-à-pas : dupliquer un récurrent ou créer un voyage avion (vitrine + catalogue en une fois)">
                    🧙 Assistant
                  </button>
                  <button onClick={() => { setShowTripForm(!showTripForm); setEditingTrip(null); setTripForm({ title: '', destination: '', description: '', transport: 'autocar', start_date: '', end_date: '', price_from: 0, price_info: '', available_spots: 0, total_spots: 0, image_url: '', program_pdf: '', program_details: '', highlights_text: '', included_text: '', not_included_text: '', gallery_images: [], image_focus: null, map_coords: null, itineraire_image: '', itinerary: [], hotels: [], important_notes: [] }); }}
                    className="bg-[#1E3A5F] text-white px-2.5 py-1 rounded-lg font-bold text-xs hover:bg-[#2d5a8f] transition"
                    data-testid="add-trip-btn">
                    {showTripForm ? 'Annuler' : '+ Nouveau'}
                  </button>
                </div>
              )}
            </div>

            {role === 'limited' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-xs text-blue-800">
                👁️ <strong>Mode lecture.</strong> Vous pouvez consulter les places disponibles pour chaque voyage. Contactez le président pour toute modification.
              </div>
            )}

            {showTripForm && role !== 'limited' && (
              <div className="bg-white rounded-xl shadow-md p-6 mb-6 border-2 border-[#79c9ea]" data-testid="trip-form">
                <div className="flex items-center justify-between gap-2 flex-wrap mb-4">
                  <h3 className="font-bold text-[#1E3A5F]">{editingTrip ? 'Modifier le voyage' : 'Nouveau voyage'}</h3>
                  {!editingTrip && (
                    <button type="button" data-testid="trip-preset-portugal" title="Pré-remplit dates, avion, formule petit-déjeuner, inclus / non inclus et notes — tu complètes hôtel, compagnie, horaires et prix"
                      onClick={() => setTripForm(f => ({ ...f, title: f.title || 'Portugal - Séjour découverte', destination: f.destination || 'Portugal', transport: 'avion',
                        start_date: '2027-10-04', end_date: '2027-10-11', price_info: 'par personne, vols + 7 nuits en formule petit-déjeuner',
                        included_text: ["Vols aller-retour au départ de Lyon (compagnie et horaires communiqués par l'association)", "7 nuits en hôtel — formule petit-déjeuner", "Accompagnateurs de l'association pendant tout le séjour", "Programme d'excursions proposé sur place (sans transport)"].join('\n'),
                        not_included_text: ["Déjeuners et dîners — formule petit-déjeuner uniquement, les autres repas sont libres", "Transports pour les excursions (bus, train, taxi) et entrées des sites payants", "Boissons et dépenses personnelles", "Assurance annulation / rapatriement (en option)", "Taxe de séjour éventuelle, à régler sur place"].join('\n'),
                        important_notes: [...(f.important_notes || []), "Formule petit-déjeuner : seuls les petits-déjeuners sont inclus, les autres repas sont libres et à votre charge.", "Excursions : un programme est proposé par l'association ; les frais de transport et les entrées ne sont pas inclus."] }))}
                      className="text-xs px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-300 text-emerald-900 font-semibold hover:bg-emerald-100">
                      🇵🇹 Préréglage Portugal 2027 (4 → 11 oct · avion · petit-déjeuner · excursions sans transport)
                    </button>
                  )}
                </div>
                <form onSubmit={saveTrip} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Titre</label>
                    <input type="text" value={tripForm.title} onChange={e => setTripForm({...tripForm, title: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg text-sm" required data-testid="trip-form-title" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Destination</label>
                    <input type="text" value={tripForm.destination} onChange={e => setTripForm({...tripForm, destination: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="trip-form-destination" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Transport</label>
                    <select value={tripForm.transport} onChange={e => setTripForm({...tripForm, transport: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg text-sm">
                      <option value="autocar">Autocar</option>
                      <option value="avion">Avion</option>
                      <option value="mixte">Mixte</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">🍴 Formule repas</label>
                    <select value={tripForm.meal_plan || ''} onChange={e => setTripForm({...tripForm, meal_plan: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="trip-form-meal-plan">
                      <option value="">— non renseignée —</option>
                      {REPAS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    {tripForm.meal_plan === 'par_etape' && <p className="text-[10px] text-amber-700 mt-1">Renseigne la formule de chaque hébergement dans le bloc 🏨 Hébergement (onglet Programme). Libellé auto : « {formuleRepasLabel(tripForm) || '…'} »</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Libellé repas affiché (optionnel — ex. « Petit déjeuner à Dubaï - Pension complète à Oman »)</label>
                    <input type="text" value={tripForm.meal_plan_label || ''} onChange={e => setTripForm({...tripForm, meal_plan_label: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="vide = libellé automatique" data-testid="trip-form-meal-plan-label" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Date départ</label>
                    <input type="date" value={tripForm.start_date} onChange={e => setTripForm({...tripForm, start_date: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="trip-form-start-date" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Date retour</label>
                    <input type="date" value={tripForm.end_date} onChange={e => setTripForm({...tripForm, end_date: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="trip-form-end-date" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Prix à partir de</label>
                    <input type="number" value={tripForm.price_from} onChange={e => setTripForm({...tripForm, price_from: parseInt(e.target.value) || 0})}
                      className="w-full px-3 py-2 border rounded-lg text-sm" />
                  </div>
                  {tripForm.multi_dates ? (
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-500 mb-1">🪑 Places PAR DATE (chaque départ) — UNE seule saisie</label>
                    <input type="number" min={0} value={tripForm.capacite_par_date} onChange={e => setTripForm({...tripForm, capacite_par_date: parseInt(e.target.value) || 0})}
                      className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="trip-form-spots-par-date" />
                    <p className="text-[10px] text-gray-500 italic mt-1">Page à plusieurs dates : ce nombre s'applique à CHAQUE départ (ex. 57). Formulaire, site et Gestion d'un voyage se mettent à jour tout seuls. Un départ différent se règle dans « Dates de ce voyage ».</p>
                  </div>
                  ) : (
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-500 mb-1">🪑 Capacité (nombre de places) — UNE seule saisie</label>
                    <input type="number" min={0} value={tripForm.total_spots} onChange={e => setTripForm({...tripForm, total_spots: parseInt(e.target.value) || 0})}
                      className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="trip-form-spots" />
                    <p className="text-[10px] text-gray-500 italic mt-1">Les places disponibles se calculent toutes seules : capacité − inscrits (formulaire, vitrine, catalogue). Rien d'autre à saisir.</p>
                  </div>
                  )}
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Info tarifs (affiché sur la carte)</label>
                    <input type="text" value={tripForm.price_info} onChange={e => setTripForm({...tripForm, price_info: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Ex : 730€ par personne (vol + hôtel)"
                      data-testid="trip-form-price-info" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
                    <textarea value={tripForm.description} onChange={e => setTripForm({...tripForm, description: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg text-sm" rows="3" data-testid="trip-form-description" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Programme détaillé</label>
                    <textarea value={tripForm.program_details} onChange={e => setTripForm({...tripForm, program_details: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg text-sm" rows="4" data-testid="trip-form-program" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Points forts (1 par ligne)</label>
                    <textarea value={tripForm.highlights_text} onChange={e => setTripForm({...tripForm, highlights_text: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg text-sm" rows="3" placeholder="Hôtel 4* pension complète&#10;Spa inclus&#10;Animations soirée"
                      data-testid="trip-form-highlights" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Inclus (1 par ligne)</label>
                    <textarea value={tripForm.included_text} onChange={e => setTripForm({...tripForm, included_text: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg text-sm" rows="4" placeholder="7 nuits chambre avec balcon&#10;Pension complète"
                      data-testid="trip-form-included" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Non inclus (1 par ligne)</label>
                    <textarea value={tripForm.not_included_text} onChange={e => setTripForm({...tripForm, not_included_text: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg text-sm" rows="4" placeholder="Boissons hors repas&#10;Dépenses personnelles"
                      data-testid="trip-form-not-included" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Titre du bloc « Bon à savoir » (déf. « 🛏️ Bon à savoir — les chambres » ; Portugal : « 🚌 Bon à savoir — les excursions »)</label>
                    <input type="text" value={tripForm.chambres_infos_titre || ''} onChange={e => setTripForm({...tripForm, chambres_infos_titre: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg text-sm mb-2" placeholder="🛏️ Bon à savoir — les chambres" data-testid="trip-form-chambres-infos-titre" />
                    <label className="block text-xs font-medium text-gray-500 mb-1">Contenu du bloc « Bon à savoir » (Espagne : lits, vue, frigo payant, attribution par la réception) — laisse vide pour ne rien afficher</label>
                    <textarea value={tripForm.chambres_infos || ''} onChange={e => setTripForm({...tripForm, chambres_infos: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg text-sm" rows="3" placeholder="Ex. : grand lit non garanti (2 lits séparés rapprochables sur demande), vue piscine non garantie, chambres attribuées par la réception, frigo payant 3 €/jour (tarif 2026)"
                      data-testid="trip-form-chambres-infos" />
                  </div>
                  {/* Iter 761 (Kamel) — vérifier le rendu public en un clic */}
                  {editingTrip && (
                    <div className="md:col-span-2 flex items-center justify-between gap-3 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2">
                      <p className="text-xs text-sky-900">Les textes ci-dessous s'affichent sur la page publique du voyage. Enregistre, puis vérifie le rendu :</p>
                      <div className="flex gap-2 shrink-0">
                        <a href={`/voyage/${editingTrip}`} target="_blank" rel="noopener noreferrer" className="text-xs font-bold bg-[#1E3A5F] text-white px-3 py-1.5 rounded-lg" data-testid="trip-form-voir-page-publique">👁️ Voir la page publique</a>
                        <button type="button" onClick={() => window.open(`/voyage/${editingTrip}`, 'apercu-mobile', 'width=400,height=850,menubar=no,toolbar=no,location=no,status=no,scrollbars=yes')}
                          className="text-xs font-bold bg-white border-2 border-[#1E3A5F] text-[#1E3A5F] px-3 py-1.5 rounded-lg" data-testid="trip-form-voir-mobile" title="Ouvre la page dans une petite fenêtre au format téléphone">📱 Version téléphone</button>
                      </div>
                    </div>
                  )}
                  {/* Iter 760 (Kamel) — accès aux autres textes de la page voyage : « Important », « Bon à savoir – Hébergement », bloc Transport */}
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-500 mb-1">⚠️ « Important » sous la description (tarif_note) — laisse vide pour ne rien afficher</label>
                    <textarea value={tripForm.tarif_note || ''} onChange={e => setTripForm({...tripForm, tarif_note: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg text-sm" rows="2" data-testid="trip-form-tarif-note" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-500 mb-1">🛏️ « Bon à savoir – Hébergement » (encadré bleu sous la description, repris sur la facture) — laisse vide pour ne rien afficher</label>
                    <textarea value={tripForm.precisions_hebergement || ''} onChange={e => setTripForm({...tripForm, precisions_hebergement: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg text-sm" rows="2" data-testid="trip-form-precisions-hebergement" />
                  </div>
                  {tripForm.transport_option && (
                    <>
                      <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-gray-500 mb-1">🚌 Bloc Transport — texte sous le titre (bagages, départs…)</label>
                        <textarea value={tripForm.transport_option.description || ''} onChange={e => setTripForm({...tripForm, transport_option: { ...tripForm.transport_option, description: e.target.value }})}
                          className="w-full px-3 py-2 border rounded-lg text-sm" rows="2" data-testid="trip-form-transport-description" />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-gray-500 mb-1">🚌 Bloc Transport — « Bonus » (ex. arrêt shopping à la frontière) — laisse vide pour ne rien afficher</label>
                        <textarea value={tripForm.transport_option.note || ''} onChange={e => setTripForm({...tripForm, transport_option: { ...tripForm.transport_option, note: e.target.value }})}
                          className="w-full px-3 py-2 border rounded-lg text-sm" rows="2" data-testid="trip-form-transport-note" />
                      </div>
                    </>
                  )}
                  {tripForm.transport_option && (
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-gray-500 mb-1">🚌 Bloc Transport — villes de départ (1 par ligne : <code>heure | ville | adresse</code>)</label>
                      <textarea value={tripForm.transport_departures_text || ''} onChange={e => setTripForm({...tripForm, transport_departures_text: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg text-sm font-mono" rows="3" placeholder="1h30 | Saint-Étienne | Devant la gare Châteaucreux" data-testid="trip-form-transport-departures" />
                    </div>
                  )}
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-500 mb-1">➖➕ Réductions et suppléments (1 par ligne ; une ligne qui commence par « Réduction » s'affiche en vert) — vide = bloc masqué</label>
                    <textarea value={tripForm.reductions_text || ''} onChange={e => setTripForm({...tripForm, reductions_text: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg text-sm" rows="3" data-testid="trip-form-reductions" />
                  </div>
                  {!tripForm.transport_option && !/avion|croisi/i.test(tripForm.transport || '') && (
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-gray-500 mb-1">🚌 Horaires et lieux de départ du car (1 par ligne : <code>heure | ville | adresse</code>) — vide = bloc masqué</label>
                      <textarea value={tripForm.departure_schedule_text || ''} onChange={e => setTripForm({...tripForm, departure_schedule_text: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg text-sm font-mono" rows="3" placeholder="3h00 | Saint-Étienne | Devant la gare Châteaucreux" data-testid="trip-form-departure-schedule" />
                    </div>
                  )}
                  {!/avion|croisi/i.test(tripForm.transport || '') && <div className="md:col-span-2 border rounded-lg p-3 bg-gray-50">
                    <label className="flex items-center gap-2 text-xs font-medium text-gray-600">
                      <input type="checkbox" checked={!!tripForm.has_car_schedule} onChange={e => setTripForm({...tripForm, has_car_schedule: e.target.checked})} data-testid="trip-form-has-car-schedule" />
                      🚌 Bloc « Horaires de départ et retour en autocar » (journées type Turin : aller + retour détaillés)
                    </label>
                    {tripForm.has_car_schedule && (
                      <div className="grid md:grid-cols-2 gap-2 mt-2">
                        <div><span className="text-[11px] text-gray-500">Aller (<code>heure | ville | adresse | note</code>)</span>
                          <textarea value={tripForm.car_aller_text || ''} onChange={e => setTripForm({...tripForm, car_aller_text: e.target.value})} className="w-full px-2 py-1.5 border rounded text-xs font-mono" rows="4" data-testid="trip-form-car-aller" /></div>
                        <div><span className="text-[11px] text-gray-500">Retour (<code>heure | ville | adresse</code> — une ligne de texte long = encadré d'info)</span>
                          <textarea value={tripForm.car_retour_text || ''} onChange={e => setTripForm({...tripForm, car_retour_text: e.target.value})} className="w-full px-2 py-1.5 border rounded text-xs font-mono" rows="4" data-testid="trip-form-car-retour" /></div>
                        <div className="md:col-span-2"><span className="text-[11px] text-gray-500">Note sous les horaires</span>
                          <input type="text" value={tripForm.car_schedule_note || ''} onChange={e => setTripForm({...tripForm, car_schedule_note: e.target.value})} className="w-full px-2 py-1.5 border rounded text-xs" data-testid="trip-form-car-note" /></div>
                      </div>
                    )}
                  </div>}
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-500 mb-1">💡 Suggestions d'activités sur place (journées — 1 par ligne : <code>nom | description</code>) — vide = bloc masqué</label>
                    <textarea value={tripForm.activity_suggestions_text || ''} onChange={e => setTripForm({...tripForm, activity_suggestions_text: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg text-sm" rows="3" placeholder="Mole Antonelliana | Le symbole de Turin, vue panoramique au sommet" data-testid="trip-form-activity-suggestions" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-500 mb-1">🏜️ Activités désert / séjour (Algérie — 1 par ligne) — vide = bloc masqué</label>
                    <textarea value={tripForm.desert_activities_text || ''} onChange={e => setTripForm({...tripForm, desert_activities_text: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg text-sm" rows="2" data-testid="trip-form-desert-activities" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="flex items-center gap-2 text-xs font-medium text-gray-600">
                      <input type="checkbox" checked={!!tripForm.spa_flash} onChange={e => setTripForm({...tripForm, spa_flash: e.target.checked})} data-testid="trip-form-spa-flash" />
                      💆 Afficher le bloc « Spa » sur la page (Lloret)
                    </label>
                  </div>
                  {tripForm.transport_option && (
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-gray-500 mb-1">🏊 Texte du bloc « Vue piscine garantie » (vide = texte standard). Le bloc « À la carte » se règle dans Espagne 2027 → Paramètres.</label>
                      <textarea value={tripForm.vue_piscine_texte || ''} onChange={e => setTripForm({...tripForm, vue_piscine_texte: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg text-sm" rows="3" data-testid="trip-form-vue-piscine" />
                    </div>
                  )}
                  {/avion|croisi/i.test(tripForm.transport || '') && (
                    <>
                      <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-gray-500 mb-1">🛏️ Tarifs par type de chambre (1 par ligne : <code>type | prix | base</code> — « base » marque la ligne de référence) — vide = bloc masqué</label>
                        <textarea value={tripForm.room_pricing_text || ''} onChange={e => setTripForm({...tripForm, room_pricing_text: e.target.value})}
                          className="w-full px-3 py-2 border rounded-lg text-sm font-mono" rows="3" placeholder="Chambre double | 1890 | base" data-testid="trip-form-room-pricing" />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-gray-500 mb-1">➕ Options payantes (1 par ligne : <code>nom | description</code>) — vide = bloc masqué</label>
                        <textarea value={tripForm.paid_options_text || ''} onChange={e => setTripForm({...tripForm, paid_options_text: e.target.value})}
                          className="w-full px-3 py-2 border rounded-lg text-sm" rows="3" data-testid="trip-form-paid-options" />
                      </div>
                    </>
                  )}
                  <TripPhotosEditor imageUrl={tripForm.image_url} gallery={tripForm.gallery_images} getAuthConfig={getAuthConfig}
                    focus={tripForm.image_focus} onFocus={v => setTripForm({ ...tripForm, image_focus: v })}
                    onImageUrl={v => setTripForm({ ...tripForm, image_url: v, image_focus: null })} onGallery={v => setTripForm({ ...tripForm, gallery_images: v })} />
                  <TripCarteEditor title={tripForm.title} coords={tripForm.map_coords} onCoords={v => setTripForm({ ...tripForm, map_coords: v })} getAuthConfig={getAuthConfig}
                    itineraireImage={tripForm.itineraire_image} onItineraireImage={v => setTripForm({ ...tripForm, itineraire_image: v })} />
                  <ItineraryEditor itinerary={tripForm.itinerary} startDate={tripForm.start_date} onChange={v => setTripForm({ ...tripForm, itinerary: v })} onImportClick={() => setShowProgImport(true)} />
                  <ProgrammeImportModal open={showProgImport} onClose={() => setShowProgImport(false)} startDate={tripForm.start_date} getAuthConfig={getAuthConfig}
                    onApply={(r) => setTripForm(prev => ({ ...prev,
                      ...(r.itinerary ? { itinerary: r.itinerary } : {}), ...(r.hotels ? { hotels: r.hotels } : {}),
                      ...(r.important_notes ? { important_notes: r.important_notes } : {}),
                      ...(r.highlights ? { highlights_text: r.highlights.join('\n') } : {}),
                      ...(r.included ? { included_text: r.included.join('\n') } : {}),
                      ...(r.not_included ? { not_included_text: r.not_included.join('\n') } : {}),
                    }))} />
                  <HotelsEditor hotels={tripForm.hotels} onChange={v => setTripForm({ ...tripForm, hotels: v })} />
                  <NotesEditor notes={tripForm.important_notes} onChange={v => setTripForm({ ...tripForm, important_notes: v })} />
                  <ExcursionsBlocEditor imageUrl={tripForm.excursions_image_url || ''} excursions={tripForm.excursions || []} onChange={v => setTripForm({ ...tripForm, ...v })} />
                  <div className="md:col-span-2 flex justify-end gap-3">
                    <button type="button" onClick={() => { setShowTripForm(false); setEditingTrip(null); }}
                      className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Annuler</button>
                    <button type="submit" className="bg-[#1E3A5F] text-white px-6 py-2 rounded-lg font-bold text-sm hover:bg-[#2a4f7a] transition"
                      data-testid="trip-form-save">{editingTrip ? 'Enregistrer' : 'Créer'}</button>
                  </div>
                </form>
              </div>
            )}

            {loadingTrips ? (
              <div className="text-center py-12 text-gray-400">Chargement...</div>
            ) : (
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                {trips.length > 0 && (() => {
                  const years = Array.from(new Set(trips.map(t => (t.start_date || '').slice(0, 4)).filter(Boolean))).sort();
                  if (years.length < 2) return null;
                  const cur = String(new Date().getFullYear());
                  return (
                    <div className="flex flex-wrap items-center gap-1" data-testid="site-trips-year-filter">
                      {['all', ...years].map(y => (
                        <button key={y} onClick={() => setTripsYearFilter(y)}
                          className={`text-[11px] px-2 py-0.5 rounded-full font-bold transition border ${tripsYearFilter === y ? 'bg-[#1E3A5F] text-white border-[#1E3A5F]' : y !== 'all' && y > cur ? 'bg-amber-50 text-amber-900 border-amber-300 hover:bg-amber-100' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'}`}
                          data-testid={`site-trips-year-${y}`}>
                          {y === 'all' ? 'Toutes' : y} <span className="opacity-60">({y === 'all' ? trips.length : trips.filter(t => (t.start_date || '').startsWith(y)).length})</span>
                        </button>
                      ))}
                    </div>
                  );
                })()}
                {trips.length > 0 && (
                  <div className="flex flex-1 min-w-[240px] items-center gap-2 bg-white border border-gray-200 rounded-lg px-2 py-1" data-testid="site-trips-quickjump-bar">
                    <span className="text-[11px] font-bold text-[#1E3A5F] whitespace-nowrap">🎯</span>
                    <select
                      value=""
                      onChange={(e) => {
                        const tripId = e.target.value;
                        if (!tripId) return;
                        const t = trips.find(x => x.id === tripId);
                        if (t && isPastSiteTrip(t)) setShowPastSiteTrips(true);
                        setTimeout(() => {
                          const target = document.querySelector(`[data-testid="trip-card-${tripId}"]`);
                          if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }, 150);
                        e.target.value = '';
                      }}
                      className="flex-1 min-w-[120px] px-2 py-1 text-xs border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-[#79c9ea] focus:border-[#79c9ea]"
                      data-testid="site-trips-quickjump-select"
                    >
                      <option value="">— Aller à un voyage —</option>
                      {(() => {
                        const byYear = {};
                        trips.forEach((t) => {
                          const yr = (t.start_date || '').slice(0, 4) || 'Sans date';
                          if (!byYear[yr]) byYear[yr] = [];
                          byYear[yr].push(t);
                        });
                        const yearsSorted = Object.keys(byYear).sort((a, b) => {
                          if (a === 'Sans date') return 1;
                          if (b === 'Sans date') return -1;
                          return b.localeCompare(a);
                        });
                        const currentYear = new Date().getFullYear().toString();
                        return yearsSorted.map(yr => {
                          const isPastYear = yr !== 'Sans date' && parseInt(yr, 10) < parseInt(currentYear, 10);
                          const label = yr === 'Sans date'
                            ? '— Sans date —'
                            : isPastYear ? `${yr} (passés)` : yr;
                          return (
                            <optgroup key={yr} label={label}>
                              {byYear[yr]
                                .sort((a, b) => (a.start_date || '').localeCompare(b.start_date || ''))
                                .map(t => {
                                  const past = isPastSiteTrip(t);
                                  return (
                                    <option key={t.id} value={t.id}>
                                      {past ? '✓ ' : ''}{t.title}{t.start_date ? ` — ${t.start_date}` : ''}
                                    </option>
                                  );
                                })}
                            </optgroup>
                          );
                        });
                      })()}
                    </select>
                    {(() => {
                      const pastCount = trips.filter(isPastSiteTrip).length;
                      if (pastCount === 0) return null;
                      return (
                        <button
                          onClick={() => setShowPastSiteTrips(s => !s)}
                          className={`text-[11px] px-2 py-1 rounded-md font-bold transition whitespace-nowrap ${showPastSiteTrips ? 'bg-amber-100 text-amber-800 border border-amber-300' : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'}`}
                          data-testid="site-trips-toggle-past-btn"
                          title={showPastSiteTrips ? 'Masquer les voyages passés / terminés' : 'Afficher aussi les voyages passés ou marqués "Terminé"'}
                        >
                          {showPastSiteTrips ? `✓ Passés (${pastCount})` : `↓ Passés (${pastCount})`}
                        </button>
                      );
                    })()}
                  </div>
                )}
                </div>
                {(() => {
                  const pieges = trips.filter(isCompletedButFuture);
                  if (!pieges.length || role === 'limited') return null;
                  return (
                    <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4 space-y-2" data-testid="completed-future-alert">
                      <p className="text-sm font-bold text-red-800">
                        ⚠️ {pieges.length} voyage{pieges.length > 1 ? 's sont marqués' : ' est marqué'} « Terminé » alors que sa date n'est pas passée
                      </p>
                      <p className="text-xs text-red-700">Ces voyages sont <strong>invisibles du site public</strong> (rubrique Voyages et formulaire). Si c'est une erreur, réactivez-les :</p>
                      {pieges.map(t => (
                        <div key={t.id} className="flex items-center justify-between flex-wrap gap-2 bg-white border border-red-200 rounded-lg px-3 py-2">
                          <span className="text-xs font-semibold text-gray-800">{t.title} <span className="text-gray-500 font-normal">({t.start_date} → {t.end_date})</span></span>
                          <button
                            onClick={async () => {
                              try {
                                await axios.put(`${API}/admin/trips/${t.id}`, { status: 'active' }, getAuthConfig());
                                fetchTrips();
                              } catch (e) { alert('Erreur'); }
                            }}
                            className="text-xs bg-green-500 text-white px-3 py-1.5 rounded-lg font-bold hover:bg-green-600 transition"
                            data-testid={`reactivate-trip-${t.id}`}>
                            ✅ Réactiver (rendre visible)
                          </button>
                        </div>
                      ))}
                    </div>
                  );
                })()}
                {trips.filter(t => (tripsYearFilter === 'all' || (t.start_date || '').startsWith(tripsYearFilter)) && (showPastSiteTrips || !isPastSiteTrip(t))).map((trip) => {
                  const avion = trip.transport === 'avion' || /avion|vol/i.test(trip.transport || '');
                  const nbSem = trip.weekly_availability ? Object.keys(trip.weekly_availability).length : 0;
                  const nbComplet = trip.weekly_availability ? Object.values(trip.weekly_availability).filter(v => v === 0).length : 0;
                  const resumeTrip = (
                    <>
                      {trip.start_date ? `${fmtIsoFr(trip.start_date)} → ${fmtIsoFr(trip.end_date)}` : trip.destination}
                      {' · '}{nbSem ? `${nbSem} sem.${nbComplet ? ` (${nbComplet} complètes)` : ''}`
                        : (trip.multi_dates && (trip.date_options || []).length > 1)
                          ? <span data-testid={`multi-dates-places-${trip.id}`}>{trip.date_options.length} dates · {trip.capacite_defaut || Math.round(trip.total_spots / trip.date_options.length)} pl. par date (restantes : {trip.date_options.map(o => o.spots ?? '—').join(' · ')})</span>
                          : `${trip.available_spots}/${trip.total_spots} pl.`}
                      {trip.redirect_to && <span className="ml-1 text-[10px] bg-violet-100 text-violet-800 px-1.5 rounded-full font-bold" title="Cette page a été regroupée : son ancien lien renvoie vers la page commune. Rien n'est supprimé." data-testid={`regroupee-badge-${trip.id}`}>🧩 REGROUPÉE → {(trips.find(x => x.id === trip.redirect_to) || {}).title || 'page commune'}</span>}
                      {trip.is_hidden_public && !trip.redirect_to && trip.status !== 'completed' && <span className="ml-1 text-[10px] bg-amber-400 text-amber-950 px-1.5 rounded-full font-bold" data-testid={`draft-badge-${trip.id}`}>📝 BROUILLON</span>}
                      {trip.status === 'completed' && (isCompletedButFuture(trip)
                        ? <span className="ml-1 text-[10px] bg-red-600 text-white px-1.5 rounded-full font-bold" data-testid={`completed-future-badge-${trip.id}`}>⚠️ TERMINÉ mais date future</span>
                        : <span className="ml-1 text-[10px] bg-gray-500 text-white px-1.5 rounded-full font-bold">TERMINÉ</span>)}
                      {getEffective(trip, 'sold_out') && <span className="ml-1 text-[10px] bg-red-100 text-red-700 px-1.5 rounded-full font-bold" title="Inscriptions fermées manuellement (verrou)">🔒 FERMÉ</span>}
                    </>
                  );
                  return (
                  <React.Fragment key={trip.id}>
                  <Repli titre={`${avion ? '✈️' : '🚌'} ${trip.title}`} resume={resumeTrip} testId={`trip-card-${trip.id}`}
                    action={role !== 'limited' ? <span className="flex items-center gap-1"><VoirCommeAdherentBtn tripId={trip.id} getAuthConfig={getAuthConfig} /><button onClick={() => editTrip(trip)} className="text-xs px-2 py-0.5 rounded bg-gray-100 hover:bg-gray-200" title="Modifier la fiche" data-testid={`edit-trip-${trip.id}`}>✏️</button></span> : null}>
                  <div className="space-y-2 pt-1">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div className="flex-grow min-w-0">
                        <p className="text-xs text-gray-500">{trip.destination} · {trip.start_date} au {trip.end_date}
                          {trip.start_date && <span className={`ml-1 text-[10px] px-1.5 rounded-full font-extrabold ${trip.start_date.slice(0, 4) === String(new Date().getFullYear()) ? 'bg-gray-100 text-gray-600' : 'bg-amber-400 text-amber-950'}`} data-testid={`admin-trip-year-${trip.id}`}>{trip.start_date.slice(0, 4)}</span>}
                        </p>
                        {/* Iter 235 (Kamel) — Vues de la page voyage */}
                        <p className="text-xs text-gray-400 mt-0.5" data-testid={`trip-vues-${trip.id}`}>
                          👁 {(tripsVues[trip.id]?.total || 0)} vue{(tripsVues[trip.id]?.total || 0) > 1 ? 's' : ''}
                          {(tripsVues[trip.id]?.semaine || 0) > 0 && <span className="text-emerald-600 font-bold"> · {tripsVues[trip.id].semaine} cette semaine</span>}
                        </p>
                        {trip.is_hidden_public && trip.status !== 'completed' && (
                          <p className="text-[11px] text-amber-800 mt-0.5">📝 Brouillon — invisible du public, clique « 👁 Publier » quand la fiche est prête.</p>
                        )}
                        {trips.filter(t => (t.title || '').trim().toLowerCase() === (trip.title || '').trim().toLowerCase()).length > 1 && (
                          <p className="text-[11px] text-red-700 font-semibold mt-0.5 flex flex-wrap items-center gap-2" data-testid={`trip-doublon-${trip.id}`}>
                            <span>⚠️ Doublon : une autre fiche porte exactement le même titre{trip.is_hidden_public ? ' — celle-ci est un brouillon.' : ' (celle-ci est la publiée).'}</span>
                            {trip.is_hidden_public && (
                              <button type="button" onClick={() => deleteTrip(trip.id, `${trip.title} (doublon brouillon)`)} className="text-[10px] px-2 py-0.5 rounded-full bg-red-600 text-white font-bold" data-testid={`trip-doublon-suppr-${trip.id}`}>🗑 Supprimer ce doublon</button>
                            )}
                          </p>
                        )}
                        {role !== 'limited' && (
                          <label className="flex items-center gap-1.5 text-[11px] text-gray-600 mt-1" title="Ville utilisée pour la météo dans l'espace adhérent (les adhérents peuvent aussi saisir une autre ville du circuit)">🌤 Ville météo
                            <input defaultValue={trip.meteo_lieu || ''} placeholder={trip.destination?.split(/[(,–-]/)[0]?.trim() || 'ex. Tipaza'} data-testid={`trip-meteo-lieu-${trip.id}`}
                              onBlur={async (e) => { const v = e.target.value.trim(); if (v === (trip.meteo_lieu || '')) return; try { await axios.put(`${API}/admin/trips/${trip.id}`, { meteo_lieu: v }, getAuthConfig()); fetchTrips(); } catch { alert('Erreur enregistrement ville météo'); } }}
                              className="border border-gray-300 rounded px-1.5 py-0.5 text-[11px] w-40" />
                          </label>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap justify-end">
                        {role !== 'limited' && (
                          <>
                            {/* Iter 235 (Kamel) — Annonce à la liste de diffusion */}
                            {trip.status === 'active' && !trip.is_hidden_public && (
                              trip.diffusion_annoncee_at ? (
                                <span className="text-[11px] bg-gray-100 text-gray-400 px-2 py-1 rounded-lg font-semibold cursor-help"
                                  title={`Annoncé (liste de diffusion + adhérents) le ${new Date(trip.diffusion_annoncee_at).toLocaleDateString('fr-FR')}`}
                                  data-testid={`annonce-fait-${trip.id}`}>📣 Annoncé</span>
                              ) : (
                                <button onClick={() => annoncerTrip(trip)}
                                  className="text-[11px] bg-pink-500 text-white px-2 py-1 rounded-lg font-semibold hover:bg-pink-600 transition"
                                  title="Envoyer ce voyage par email à la liste de diffusion + tous les adhérents (1 seule fois)"
                                  data-testid={`annonce-trip-${trip.id}`}>📣 Annoncer</button>
                              )
                            )}
                            <button onClick={() => togglePublishTrip(trip)}
                              className={`text-[11px] px-2 py-1 rounded-lg font-bold transition ${trip.is_hidden_public ? 'bg-emerald-600 text-white hover:bg-emerald-700 animate-pulse' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                              title={trip.is_hidden_public ? 'Rendre visible sur le site + formulaire d\'inscription' : 'Masquer du site (la gestion reste intacte)'}
                              data-testid={`publish-trip-${trip.id}`}>{trip.is_hidden_public ? '👁 Publier' : '🙈 Masquer'}</button>
                            <a href={`/voyage/${trip.id}`} target="_blank" rel="noreferrer"
                              className="text-[11px] bg-white border border-gray-300 text-gray-700 px-2 py-1 rounded-lg font-semibold hover:bg-gray-50 transition"
                              title="Voir la page publique (fonctionne même en brouillon)" data-testid={`view-trip-${trip.id}`}>🔗 Page</a>
                            {(trip.flight_info || avion) && (
                              <button onClick={() => setFlightInfoTrip(trip)}
                                className="text-[11px] bg-indigo-500 text-white px-2 py-1 rounded-lg font-semibold hover:bg-indigo-600 transition"
                                title="Modifier les horaires de vols, le RDV aéroport, bagages et formalités"
                                data-testid={`flight-info-trip-${trip.id}`}>✈️ Vols</button>
                            )}
                            <button
                              onClick={() => { setAssistantSource(trip); setShowAssistantVoyage(true); }}
                              className="text-[11px] bg-purple-500 text-white px-2 py-1 rounded-lg font-semibold hover:bg-purple-600 transition"
                              title="Cloner ce voyage pour l'année suivante via l'assistant : fiche + formule d'inscription, dates du programme recalées, en brouillon"
                              data-testid={`clone-trip-${trip.id}`}>
                              📋 Cloner
                            </button>
                            <button onClick={async () => {
                                const newStatus = trip.status === 'completed' ? 'active' : 'completed';
                                try {
                                  await axios.put(`${API}/admin/trips/${trip.id}`, { status: newStatus }, getAuthConfig());
                                  fetchTrips();
                                } catch (e) { alert('Erreur'); }
                              }}
                              className={`text-[11px] px-2 py-1 rounded-lg font-semibold transition ${
                                trip.status === 'completed' ? 'bg-green-500 text-white hover:bg-green-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                              }`}
                              data-testid={`status-trip-${trip.id}`}>
                              {trip.status === 'completed' ? 'Réactiver' : 'Terminer'}
                            </button>
                            <button onClick={() => setTripToDelete(trip)}
                              className="text-[11px] bg-white border border-red-300 text-red-600 px-2 py-1 rounded-lg font-semibold hover:bg-red-50 transition"
                              title="Suppression voyage : retrait public OU suppression intégrale"
                              data-testid={`delete-trip-${trip.id}`}>
                              🗑
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Places standard — Iter 438 (Kamel) : compteur MANUEL supprimé, tout passe
                        par la capacité 🪑 du Catalogue voyages (compteur automatique) */}
                    {!trip.weekly_availability && trip.multi_dates && (trip.date_options || []).length > 1 && (
                      <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-2.5 py-1.5 flex-wrap text-xs" data-testid={`spots-par-date-${trip.id}`}>
                        <span className="text-gray-500 font-medium">Places par date :</span>
                        {trip.date_options.map((o, i) => (
                          <span key={o.fd_id || i} className={`font-bold ${(o.spots ?? 0) <= 0 ? 'text-red-600' : (o.spots ?? 0) <= 5 ? 'text-orange-600' : 'text-green-700'}`} data-testid={`spots-date-${trip.id}-${i}`}>
                            {o.label || o.start_date} : {o.spots ?? '—'} / {trip.capacite_defaut || Math.round((trip.total_spots || 0) / trip.date_options.length)}
                          </span>
                        ))}
                        <span className="text-[10px] text-gray-500">🔒 automatique</span>
                      </div>
                    )}
                    {!trip.weekly_availability && !(trip.multi_dates && (trip.date_options || []).length > 1) && (
                      <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-2.5 py-1.5 flex-wrap text-xs">
                        <span className="text-gray-500 font-medium">Places :</span>
                        <span className={`text-sm font-bold ${trip.available_spots <= 0 ? 'text-red-600' : trip.available_spots <= 5 ? 'text-orange-600' : 'text-green-700'}`} data-testid={`spots-display-${trip.id}`}>
                          {trip.available_spots}
                        </span>
                        <span className="text-gray-400">/ {trip.total_spots}</span>
                        {trip.available_spots <= 0 && <span className="text-[10px] bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-bold">LISTE D'ATTENTE</span>}
                        <span className="text-[10px] text-gray-500" title="Le compteur est automatique : capacité 🪑 (Catalogue voyages) − inscrits. Les annulations libèrent les places toutes seules.">
                          🔒 automatique (capacité 🪑 dans Inscriptions & tarifs)
                        </span>
                      </div>
                    )}

                    {/* Iter 695 (Kamel) — Espagne 2027 fusionnée : places 100 % automatiques (3 compteurs), plus de saisie manuelle */}
                    {nbSem > 0 && trip.transport_option?.fusion && (
                      <Repli titre="🗓️ Places par semaine (automatique)" resume={`${(trip.weekly_pricing || []).length} semaines · 🏨 sans transport / 🛫 aller / 🛬 retour · calcul auto`} testId={`weekly-section-${trip.id}`}>
                        <div className="pt-1 space-y-1">
                          <p className="text-[11px] text-emerald-800 font-semibold">🔒 Rien à saisir ici : les places se calculent toutes seules (quota sans transport et sièges des cars − inscrits). Réglages dans Centre de contrôle → Voyages → panel Espagne (Tarifs et Cars 2027).</p>
                          <CompteursBloc getAuthConfig={getAuthConfig} type={/printemps|4 jours/i.test(trip.title || '') ? '4j' : '8j'} titre="" />
                        </div>
                      </Repli>
                    )}
                    {/* Places par semaine (Lloret Été) — saisie manuelle (saisons sans compteurs automatiques) */}
                    {nbSem > 0 && !trip.transport_option?.fusion && (
                      <Repli titre="🗓️ Places par semaine" resume={`${nbSem} semaines · ${nbComplet} complètes · ${Object.values(trip.weekly_availability).filter(v => v === -1).length} dispo`} testId={`weekly-section-${trip.id}`}>
                        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap pt-1">
                          <p className="text-sm font-bold text-[#1E3A5F]">Places par semaine :</p>
                          {/* Iter 156c — Actions en masse pour gagner du temps */}
                          {role !== 'limited' && (
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={async () => {
                                  if (!window.confirm('Marquer TOUTES les semaines comme COMPLET ?\n\nLe message WhatsApp "Liste d\'attente" listera toutes les semaines.')) return;
                                  const allFull = Object.fromEntries(Object.keys(trip.weekly_availability).map(k => [k, 0]));
                                  try {
                                    await axios.patch(`${API}/admin/trips/${trip.id}/weekly`, { weekly_availability: allFull }, getAuthConfig());
                                    setTrips(prev => prev.map(t => t.id === trip.id ? { ...t, weekly_availability: allFull } : t));
                                  } catch (err) { alert('Erreur : ' + (err.response?.data?.detail || err.message)); }
                                }}
                                className="text-[10px] font-bold px-2 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200 transition"
                                title="Marquer TOUTES les semaines comme COMPLET (action en masse)"
                                data-testid={`weekly-bulk-full-${trip.id}`}
                              >🔒 Tout COMPLET</button>
                              <button
                                type="button"
                                onClick={async () => {
                                  if (!window.confirm('Remettre TOUTES les semaines en DISPONIBLE (∞) ?')) return;
                                  const allOpen = Object.fromEntries(Object.keys(trip.weekly_availability).map(k => [k, -1]));
                                  try {
                                    await axios.patch(`${API}/admin/trips/${trip.id}/weekly`, { weekly_availability: allOpen }, getAuthConfig());
                                    setTrips(prev => prev.map(t => t.id === trip.id ? { ...t, weekly_availability: allOpen } : t));
                                  } catch (err) { alert('Erreur : ' + (err.response?.data?.detail || err.message)); }
                                }}
                                className="text-[10px] font-bold px-2 py-1 rounded bg-green-100 text-green-700 hover:bg-green-200 transition"
                                title="Remettre TOUTES les semaines en DISPONIBLE (∞)"
                                data-testid={`weekly-bulk-open-${trip.id}`}
                              >🔓 Tout DISPO</button>
                            </div>
                          )}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {Object.entries(trip.weekly_availability)
                            .sort(([a], [b]) => {
                              const ia = WEEK_ORDER.indexOf(a);
                              const ib = WEEK_ORDER.indexOf(b);
                              return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
                            })
                            .map(([weekKey, spots]) => (
                            <div key={weekKey} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2" data-testid={`weekly-row-${weekKey}`}>
                              <span className="text-xs text-gray-600 font-medium">{WEEK_LABELS[weekKey] || weekKey}</span>
                              <div className="flex items-center gap-2">
                                {role === 'limited' ? (
                                  <span className={`w-16 text-center font-bold text-xs ${spots === 0 ? 'text-red-600' : spots === -1 ? 'text-green-700' : spots <= 5 ? 'text-orange-600' : 'text-[#1E3A5F]'}`} data-testid={`weekly-display-${weekKey}`}>
                                    {spots === -1 ? '∞' : spots}
                                  </span>
                                ) : (
                                  <>
                                    {/* Iter 156c (Kamel — Espagne été liste d'attente)
                                        Boutons toggle ultra-rapides pour les états les plus fréquents :
                                        COMPLET (0) et DISPONIBLE (-1 = ∞). L'input number reste
                                        pour les cas "X dernières places". */}
                                    <button
                                      type="button"
                                      onClick={() => updateWeeklySpots(trip.id, weekKey, '-1')}
                                      className={`text-[10px] font-bold px-2 py-1 rounded transition ${spots === -1 ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-600 hover:bg-green-100 hover:text-green-700'}`}
                                      title="Marquer comme DISPONIBLE (illimité ∞)"
                                      data-testid={`weekly-set-open-${weekKey}`}
                                    >🔓 OK</button>
                                    <button
                                      type="button"
                                      onClick={() => updateWeeklySpots(trip.id, weekKey, '0')}
                                      className={`text-[10px] font-bold px-2 py-1 rounded transition ${spots === 0 ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-600 hover:bg-red-100 hover:text-red-700'}`}
                                      title="Marquer comme COMPLET (déclenche le bouton WhatsApp liste d'attente)"
                                      data-testid={`weekly-set-full-${weekKey}`}
                                    >🔒 Complet</button>
                                    <input type="number" key={spots} defaultValue={spots} min="-1"
                                      className="w-14 px-1.5 py-1 border rounded text-center font-bold text-xs"
                                      onBlur={(e) => { if (e.target.value !== spots.toString()) updateWeeklySpots(trip.id, weekKey, e.target.value); }}
                                      title="Saisir un nombre précis de places restantes (ex: 3 = 3 dernières)"
                                      data-testid={`weekly-input-${weekKey}`} />
                                  </>
                                )}
                                {spots === -1 && <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-bold">DISPO</span>}
                                {spots === 0 && <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-bold">COMPLET</span>}
                                {spots !== -1 && spots > 0 && spots <= 5 && <span className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-bold">DERNIÈRES PL.</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </Repli>
                    )}

                    {/* Iter 937 — dates d'une page commune : ajout / rattachement / regroupement (déclinaisons inchangées) */}
                    {role !== 'limited' && <DatesVoyageAdmin trip={trip} trips={trips} getAuthConfig={getAuthConfig} onChange={fetchTrips} />}
                    {/* Iter 152ab — Édition des dates multiples (Turin / voyages mono-jour récurrents) */}
                    {role !== 'limited' && Array.isArray(trip.date_options) && trip.date_options.length > 0 && (
                      <Repli titre="📅 Dates multiples" resume={`${trip.date_options.length} dates · ${trip.date_options.filter(o => o.status === 'complet').length} complètes`} testId={`date-options-editor-${trip.id}`}>
                        <div className="space-y-1 pt-1">
                          {trip.date_options.map((opt, idx) => (
                            <div key={idx} className="flex items-center gap-2 bg-white rounded px-2 py-1 text-xs">
                              <span className="flex-1 font-mono">{opt.label}</span>
                              {opt.fd_id ? (
                                <span className="text-[11px] text-gray-600" data-testid={`date-opt-auto-${trip.id}-${idx}`}>{opt.spots ?? '—'} pl. restantes · 🔒 auto</span>
                              ) : (<>
                              <select defaultValue={opt.status || 'disponible'}
                                className="border rounded px-1 py-0.5 text-[11px]"
                                data-testid={`date-opt-status-${trip.id}-${idx}`}
                                onChange={async (e) => {
                                  const newOpts = trip.date_options.map((o, i) => i === idx ? { ...o, status: e.target.value } : o);
                                  try {
                                    await axios.patch(`${API}/admin/trips/${trip.id}`, { date_options: newOpts }, getAuthConfig());
                                    setTrips(prev => prev.map(t => t.id === trip.id ? { ...t, date_options: newOpts } : t));
                                  } catch (err) {
                                    alert('Erreur : ' + (err.response?.data?.detail || err.message));
                                  }
                                }}>
                                <option value="disponible">Disponible</option>
                                <option value="liste_attente">Liste attente</option>
                                <option value="complet">Complet</option>
                              </select>
                              <input type="number" defaultValue={opt.spots ?? ''} min="0" placeholder="—"
                                className="w-14 px-1 py-0.5 border rounded text-center text-[11px]"
                                data-testid={`date-opt-spots-${trip.id}-${idx}`}
                                onBlur={async (e) => {
                                  const v = e.target.value === '' ? null : parseInt(e.target.value);
                                  const newOpts = trip.date_options.map((o, i) => i === idx ? { ...o, spots: v } : o);
                                  try {
                                    await axios.patch(`${API}/admin/trips/${trip.id}`, { date_options: newOpts }, getAuthConfig());
                                    setTrips(prev => prev.map(t => t.id === trip.id ? { ...t, date_options: newOpts } : t));
                                  } catch (err) {
                                    alert('Erreur : ' + (err.response?.data?.detail || err.message));
                                  }
                                }} />
                              </>)}
                              <button type="button"
                                className="text-rose-600 hover:text-rose-800 text-[11px] font-bold"
                                data-testid={`date-opt-remove-${trip.id}-${idx}`}
                                onClick={async () => {
                                  if (!window.confirm(`Retirer "${opt.label}" de cette page ?\n\nLa déclinaison, ses inscriptions et ses plans de bus restent intacts (Principe N°1).`)) return;
                                  try {
                                    if (opt.fd_id) {
                                      // Iter 947 — départ rattaché : on le détache de la page (sinon la synchro auto le remet)
                                      const r = await axios.delete(`${API}/admin/trips/${trip.id}/dates/${encodeURIComponent(opt.fd_id)}`, getAuthConfig());
                                      const restantes = (r.data.options || []);
                                      setTrips(prev => prev.map(t => t.id === trip.id ? { ...t, date_options: restantes, multi_dates: r.data.multi_dates } : t));
                                      return;
                                    }
                                    const newOpts = trip.date_options.filter((_, i) => i !== idx);
                                    await axios.patch(`${API}/admin/trips/${trip.id}`, { date_options: newOpts }, getAuthConfig());
                                    setTrips(prev => prev.map(t => t.id === trip.id ? { ...t, date_options: newOpts } : t));
                                  } catch (err) {
                                    alert('Erreur : ' + (err.response?.data?.detail || err.message));
                                  }
                                }}>
                                🗑
                              </button>
                            </div>
                          ))}
                        </div>
                        <p className="text-[10px] text-gray-500 italic mt-1.5">
                          Astuce : pour que le voyage entier disparaisse du public quand une date est passée, retire la ligne avec 🗑. La gestion admin et les inscriptions restent intactes (Principe N°1).
                        </p>
                      </Repli>
                    )}

                    {/* Horodatage dernière modification des places */}
                    {trip.spots_updated_at && (
                      <p className="text-[11px] text-gray-400 italic mt-1" data-testid={`spots-updated-at-${trip.id}`}>
                        Places modifiées le {new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(trip.spots_updated_at))}
                        {trip.spots_updated_by ? ` par ${trip.spots_updated_by}` : ''}
                      </p>
                    )}

                    {/* ===== CHAMBRE INDIVIDUELLE ===== */}
                    {role !== 'limited' && (() => {
                      // Si voyage avec weekly_availability (Lloret été), on propose une gestion par semaine
                      const isWeekly = !!trip.weekly_availability && Object.keys(trip.weekly_availability).length > 0;
                      const weeklyData = getEffective(trip, 'chambre_ind_weekly') || {};
                      const setWeekly = (weekKey, patch) => {
                        const next = { ...weeklyData, [weekKey]: { ...(weeklyData[weekKey] || {}), ...patch } };
                        setDraft(trip.id, { chambre_ind_weekly: next });
                      };
                      if (isWeekly) {
                        const weeks = Object.keys(trip.weekly_availability).sort((a, b) => {
                          const ia = WEEK_ORDER.indexOf(a);
                          const ib = WEEK_ORDER.indexOf(b);
                          return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
                        });
                        const nbOff = weeks.filter(wk => weeklyData[wk]?.disabled).length;
                        return (
                          <Repli titre="🛏️ Chambre individuelle" resume={`par semaine · ${nbOff ? `${nbOff} complètes` : 'toutes dispo'}`} testId={`chambre-ind-block-${trip.id}`}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-1">
                              {weeks.map(wk => {
                                const w = weeklyData[wk] || {};
                                const disabled = !!w.disabled;
                                const montant = w.montant ?? '';
                                return (
                                  <div key={wk} className="flex items-center justify-between gap-2 bg-white rounded px-2 py-1.5 border border-purple-100">
                                    <span className="text-[11px] font-medium text-purple-900 truncate">{WEEK_LABELS[wk] || wk}</span>
                                    <div className="flex items-center gap-1.5">
                                      <label className="flex items-center gap-1 cursor-pointer" title="Plus de chambre ind. dispo sur cette semaine">
                                        <input type="checkbox" checked={disabled}
                                          onChange={e => setWeekly(wk, { disabled: e.target.checked })}
                                          data-testid={`chambre-ind-weekly-disabled-${trip.id}-${wk}`} />
                                        <span className="text-[10px] text-purple-700">{disabled ? 'COMPLET' : 'Dispo'}</span>
                                      </label>
                                      <input type="number" min="0"
                                        value={montant}
                                        onChange={e => setWeekly(wk, { montant: e.target.value === '' ? null : Number(e.target.value) })}
                                        placeholder="€"
                                        className="w-14 px-1.5 py-0.5 border border-purple-300 rounded text-[11px] text-center"
                                        data-testid={`chambre-ind-weekly-montant-${trip.id}-${wk}`} />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            <p className="text-[10px] text-gray-500 italic mt-1.5">Chaque semaine Lloret est un voyage distinct — cochez "COMPLET" pour masquer l'option chambre individuelle sur la semaine concernée. <strong>Laissez le montant vide</strong> pour appliquer le tarif par défaut du voyage (100 € pour 4j, 220 € pour 8j). Saisir « 0 » est traité comme « vide ».</p>
                          </Repli>
                        );
                      }
                      // Voyage standard (non weekly) — toggle global
                      const ciOff = !!getEffective(trip, 'chambre_ind_disabled'), ciMontant = getEffective(trip, 'chambre_ind_montant');
                      return (
                        <Repli titre="🛏️ Chambre individuelle" resume={ciOff ? '🔒 plus disponible' : `dispo · supplément ${ciMontant ?? 'par défaut'}${ciMontant != null ? ' €' : ''}`} testId={`chambre-ind-block-${trip.id}`}>
                          <div className="flex items-center justify-between flex-wrap gap-2 pt-1">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input type="checkbox" checked={!!getEffective(trip, 'chambre_ind_disabled')}
                                onChange={(e) => setDraft(trip.id, { chambre_ind_disabled: e.target.checked })}
                                data-testid={`chambre-ind-disabled-${trip.id}`} />
                              <span className="text-xs text-purple-900 font-bold">Plus de chambre individuelle disponible</span>
                            </label>
                            <div className="flex items-center gap-2">
                              <label className="text-[11px] text-purple-800">Supplément (€) :</label>
                              <input type="number" min="0"
                                value={getEffective(trip, 'chambre_ind_montant') ?? ''}
                                onChange={(e) => setDraft(trip.id, { chambre_ind_montant: e.target.value === '' ? null : Number(e.target.value) })}
                                placeholder="—"
                                className="w-20 px-2 py-1 border border-purple-300 rounded text-xs text-center font-bold"
                                data-testid={`chambre-ind-montant-${trip.id}`} />
                            </div>
                          </div>
                          {getEffective(trip, 'chambre_ind_disabled') && (
                            <p className="text-[10px] text-purple-700 italic mt-1">🔒 Option masquée sur le formulaire d'inscription.</p>
                          )}
                          <p className="text-[10px] text-gray-500 italic mt-1">Laissez le montant vide (ou « — ») pour appliquer le tarif par défaut du voyage. Saisir « 0 » est traité comme « vide ».</p>
                        </Repli>
                      );
                    })()}

                    {/* ===== TARIFS & DISPONIBILITÉS (iter 73 + brouillon iter 75quater) ===== */}
                    {role !== 'limited' && (() => {
                      const isDayTrip = !!(trip.start_date && trip.end_date && trip.start_date === trip.end_date);
                      const rawTitle = (trip.title || trip.destination || '');
                      // Iter 77ter : normaliser les accents avant regex (Thaïlande / Algérie / Sénégal matchent bien).
                      const normalizedTitle = rawTitle.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');
                      const isAvionTrip = trip.transport === 'avion' || /thailande|new.?york|istanbul|algerie|moyen|senegal|vietnam|emirats|oman|egypte/.test(normalizedTitle);
                      const babyLabel = isDayTrip ? 'N/A (journée)' : (isAvionTrip ? '(< 2 ans — avion)' : '(< 3 ans — car/nuitée)');
                      return (
                        <Repli titre="💶 Tarifs & disponibilités" resume={`${getEffective(trip, 'sold_out') ? '🔒 FERMÉ · ' : ''}réd. 3-11 ${trip.reduction_3_11 ?? '—'} €${!isDayTrip ? ` · bébé ${trip.reduction_bebe ?? '—'} €` : ''}`} testId={`trip-pricing-config-${trip.id}`}>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-1">
                            {/* Iter 411 (fusion catalogues) — réductions en LECTURE SEULE : synchronisées depuis 📋 Inscriptions & tarifs */}
                            <div className="flex items-center gap-2 text-[11px] text-emerald-900" data-testid={`trip-reduction-3-11-${trip.id}`}>
                              <span className="flex-1">Réduction 3-11 ans :</span>
                              <span className="font-bold">{trip.reduction_3_11 ?? '—'} {trip.reduction_3_11 != null ? '€' : ''}</span>
                              <span title="Se modifie dans la section 📋 Inscriptions & tarifs ci-dessous (source unique)">🔒</span>
                            </div>
                            {!isDayTrip && (
                              <div className="flex items-center gap-2 text-[11px] text-emerald-900" data-testid={`trip-reduction-bebe-${trip.id}`}>
                                <span className="flex-1">Réduction bébé {babyLabel} :</span>
                                <span className="font-bold">{trip.reduction_bebe ?? '—'} {trip.reduction_bebe != null ? '€' : ''}</span>
                                <span title="Se modifie dans la section 📋 Inscriptions & tarifs ci-dessous (source unique)">🔒</span>
                              </div>
                            )}
                            {!isDayTrip && (
                              <label className="flex items-center gap-2 text-[11px] text-emerald-900 cursor-pointer">
                                <input type="checkbox" checked={getEffective(trip, 'triple_disponible') !== false}
                                  onChange={(e) => setDraft(trip.id, { triple_disponible: e.target.checked })}
                                  data-testid={`trip-triple-disponible-${trip.id}`} />
                                <span>Chambre triple possible</span>
                              </label>
                            )}
                            {!isDayTrip && (
                              <label className="flex items-center gap-2 text-[11px] text-emerald-900 cursor-pointer">
                                <input type="checkbox" checked={getEffective(trip, 'quadruple_disponible') !== false}
                                  onChange={(e) => setDraft(trip.id, { quadruple_disponible: e.target.checked })}
                                  data-testid={`trip-quadruple-disponible-${trip.id}`} />
                                <span>Chambre quadruple possible</span>
                              </label>
                            )}
                            <label className="flex items-center gap-2 text-[11px] font-bold text-red-800 cursor-pointer md:col-span-2">
                              <input type="checkbox" checked={!!getEffective(trip, 'sold_out')}
                                onChange={(e) => setDraft(trip.id, { sold_out: e.target.checked })}
                                data-testid={`trip-sold-out-${trip.id}`} />
                              <span>🔒 Fermer les inscriptions — force COMPLET / liste d'attente partout, quel que soit le compteur (verrou manuel : car non confirmé, arrêt anticipé…)</span>
                            </label>
                            <p className="text-[10px] text-gray-500 italic md:col-span-2">ℹ️ Le nombre de places se règle UNE fois dans la fiche (✏️ → Capacité) : le compteur automatique (capacité − inscrits) alimente le formulaire, la vitrine et la « place disponible » du catalogue. Cette case n'est qu'un verrou.</p>
                          </div>
                          <p className="text-[10px] text-gray-500 italic mt-1">
                            🔒 Les réductions se gèrent dans « 📋 Inscriptions & tarifs » ci-dessous (une seule saisie, synchronisée partout).
                            {isDayTrip && ' Journée : -5 € par défaut pour les moins de 12 ans (pas de tarif bébé spécifique).'}
                          </p>
                        </Repli>
                      );
                    })()}

                    {/* ===== Iter 411 — 📋 INSCRIPTIONS & TARIFS (fusion Catalogue voyages) ===== */}
                    {role !== 'limited' && (
                      <Repli titre="📋 Inscriptions & tarifs" resume="catalogue · capacité 🪑 · réductions · formule" testId={`trip-catalogue-repli-${trip.id}`}>
                        <Suspense fallback={<div className="mt-3 text-xs text-gray-400">Chargement du catalogue…</div>}>
                          <TripCatalogueSection trip={trip} getAuthConfig={getAuthConfig} onChanged={fetchTrips} />
                        </Suspense>
                      </Repli>
                    )}

                    {/* ===== PDF DESCRIPTIF (iter 78sex) — édition admin + téléchargement + compteur ===== */}
                    {role !== 'limited' && (
                      <Repli titre="📄 PDF descriptif" resume={`${trip.descriptif_downloads || 0} téléchargement${(trip.descriptif_downloads || 0) > 1 ? 's' : ''} · notes admin · précisions hébergement`} testId={`trip-pdf-config-${trip.id}`}
                        action={<a href={`${API}/trips/${trip.id}/descriptif-pdf`} download className="text-[11px] bg-[#1E3A5F] text-white px-2 py-0.5 rounded font-semibold hover:bg-[#2d5a8f] transition" data-testid={`admin-download-descriptif-${trip.id}`} title="Télécharger le PDF descriptif (auto-généré depuis cette fiche voyage)">⬇ PDF</a>}>
                        <div className="pt-1">
                          <span className="hidden" data-testid={`trip-descriptif-downloads-${trip.id}`}>{trip.descriptif_downloads || 0}</span>
                          {/* Iter 912 (Kamel) — plus de « notes complémentaires » réservées au PDF : le descriptif reprend strictement la page publique du voyage */}
                          <p className="text-[10px] text-sky-800 bg-sky-50 border border-sky-200 rounded p-2" data-testid={`trip-pdf-regle-${trip.id}`}>
                            📄 Le PDF descriptif reprend <strong>strictement</strong> les informations de la page publique de ce voyage (présentation, programme, hôtels, repas, prix, formalités…). Pour changer le PDF, modifie la fiche voyage ci-dessus.
                          </p>
                        </div>
                        {/* Iter 152ae — Précisions hébergement (chambres triples, etc.) */}
                        <div className="mt-3 bg-amber-50 border border-amber-300 rounded p-2" data-testid={`trip-precisions-hebergement-${trip.id}`}>
                          <label className="text-[10px] font-bold text-amber-900 block mb-1">
                            🛏️ Précisions hébergement (visible sur la page voyage publique ET la facture)
                          </label>
                          <textarea
                            value={getEffective(trip, 'precisions_hebergement') ?? ''}
                            onChange={(e) => setDraft(trip.id, { precisions_hebergement: e.target.value })}
                            rows={3}
                            placeholder="Ex : Les chambres triples sont composées d'un grand lit et d'un lit individuel escamotable dans une armoire (ce ne sont pas des chambres à trois lits séparés)."
                            className="w-full text-xs border border-amber-400 rounded p-2 bg-white focus:outline-none focus:border-amber-600"
                            data-testid={`trip-precisions-hebergement-input-${trip.id}`}
                          />
                          <p className="text-[10px] text-amber-700 italic mt-1">
                            Apparaît automatiquement dans un encadré "Bon à savoir" sur la page voyage publique ET dans le PDF facture en fin de récap voyageur.
                          </p>
                        </div>
                      </Repli>
                    )}
                  </div>
                  </Repli>

                    {/* ===== Barre Enregistrer brouillon (iter 75quater) ===== */}
                    {isTripDirty(trip) && (
                      <div className="sticky bottom-2 bg-amber-50 border-2 border-amber-400 rounded-lg p-3 flex items-center justify-between shadow-lg z-10" data-testid={`trip-save-bar-${trip.id}`}>
                        <div className="flex items-center gap-2">
                          <span className="text-lg">⚠</span>
                          <div>
                            <p className="text-xs font-bold text-amber-900">Modifications non enregistrées</p>
                            <p className="text-[10px] text-amber-700">{Object.keys(tripDrafts[trip.id] || {}).length} champ(s) modifié(s)</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => resetDraft(trip.id)}
                            className="text-xs text-gray-600 hover:text-gray-800 px-2 py-1"
                            data-testid={`trip-cancel-${trip.id}`}>
                            Annuler
                          </button>
                          <button onClick={() => saveTripDraft(trip)} disabled={savingTripId === trip.id}
                            className="bg-amber-600 text-white hover:bg-amber-700 px-3 py-1.5 rounded text-xs font-bold disabled:opacity-50"
                            data-testid={`trip-save-${trip.id}`}>
                            {savingTripId === trip.id ? 'Enregistrement…' : '💾 Enregistrer les modifications'}
                          </button>
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ===== ACTUALITES ===== */}
        {activeTab === 'communication' && (
          <Suspense fallback={<div className="text-center py-8 text-gray-400 text-sm">Chargement…</div>}><CommunicationTab getAuthConfig={getAuthConfig} /></Suspense>
        )}
        {activeTab === 'accueil' && (
          <Suspense fallback={<div className="text-center py-8 text-gray-400 text-sm">Chargement…</div>}><AccueilAffichesTab getAuthConfig={getAuthConfig} /></Suspense>
        )}
        {activeTab === 'news' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-[#1E3A5F]">Gestion des actualités</h2>
              <button onClick={() => setShowNewsForm(!showNewsForm)}
                className="bg-[#79c9ea] text-white px-4 py-2 rounded-lg font-bold text-sm hover:bg-[#5ba8c8] transition"
                data-testid="add-news-btn">
                {showNewsForm ? 'Annuler' : '+ Nouvelle actualité'}
              </button>
            </div>
            {showNewsForm && (
              <div className="bg-white rounded-xl shadow-md p-6 mb-6 border-2 border-[#79c9ea]">
                <form onSubmit={createNews} className="space-y-4">
                  <input type="text" placeholder="Titre" value={newsForm.title}
                    onChange={(e) => setNewsForm({...newsForm, title: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg text-sm" required data-testid="news-form-title" />
                  <textarea placeholder="Contenu" value={newsForm.content}
                    onChange={(e) => setNewsForm({...newsForm, content: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg text-sm" rows="4" required data-testid="news-form-content" />
                  <div className="flex items-center gap-4">
                    <select value={newsForm.category} onChange={(e) => setNewsForm({...newsForm, category: e.target.value})}
                      className="px-3 py-2 border rounded-lg text-sm">
                      <option value="info">Information</option>
                      <option value="alerte">Alerte</option>
                      <option value="promotion">Promotion</option>
                      <option value="nouveau">Nouveau</option>
                    </select>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={newsForm.published}
                        onChange={(e) => setNewsForm({...newsForm, published: e.target.checked})} className="w-4 h-4" />
                      Publier
                    </label>
                    <button type="submit" className="ml-auto bg-[#1E3A5F] text-white px-4 py-2 rounded-lg font-bold text-sm"
                      data-testid="news-form-save">Créer</button>
                  </div>
                </form>
              </div>
            )}
            {loadingNews ? <div className="text-center py-12 text-gray-400">Chargement...</div> : (
              <div className="space-y-3">
                {news.map((item) => (
                  <div key={item.id} className="bg-white rounded-xl shadow-sm p-4 border border-gray-100 flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          item.category === 'info' ? 'bg-blue-100 text-blue-800' :
                          item.category === 'alerte' ? 'bg-red-100 text-red-800' :
                          item.category === 'promotion' ? 'bg-green-100 text-green-800' : 'bg-purple-100 text-purple-800'
                        }`}>{item.category}</span>
                        {!item.published && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Brouillon</span>}
                      </div>
                      <h3 className="font-bold text-[#1E3A5F]">{item.title}</h3>
                      <p className="text-sm text-gray-500 mt-1">{item.content?.substring(0, 100)}{item.content?.length > 100 ? '...' : ''}</p>
                    </div>
                    <button onClick={() => deleteNews(item.id)}
                      className="text-xs text-red-400 hover:text-red-600 font-medium ml-4"
                      data-testid={`delete-news-${item.id}`}>Supprimer</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Iter 155 — Suspense englobant tous les onglets lazy-loadés.
            Affiche le spinner RAAV pendant le téléchargement du chunk JS. */}
        <Suspense fallback={<TabLoadingFallback />}>
        {/* Iter 395 — TVA nette du trimestre en cours, dès l'ouverture du groupe Finances */}
        {['sante_fiscale', 'cheques_attente', 'remises', 'pointage_banque', 'prelevements', 'depenses', 'caisse', 'ancv', 'association', 'annulations', 'bilan', 'suivi_tva', 'audit_compta'].includes(activeTab) && (
          <TvaTrimestreBanner getAuthConfig={getAuthConfig} />
        )}
        {/* ===== INSCRIPTIONS ===== */}
        {activeTab === 'registrations' && <RegistrationsTab getAuthConfig={getAuthConfig} role={role} />}

        {/* Iter 150 — PROSPECTION */}
        {activeTab === 'prospection' && <ProspectionTab getAuthConfig={getAuthConfig} />}

        {/* ===== GESTION PAR VOYAGE ===== */}
        {activeTab === 'gestion' && <GestionVoyages getAuthConfig={getAuthConfig} />}

        {/* ===== ASSOCIATION ===== */}
        {activeTab === 'association' && <AssociationTab getAuthConfig={getAuthConfig} />}

        {/* ===== BILAN ANNUEL ===== */}
        {activeTab === 'bilan' && <BilanAnnuelTab getAuthConfig={getAuthConfig} />}
        {activeTab === 'suivi_tva' && <SuiviTvaTab getAuthConfig={getAuthConfig} />}
        {activeTab === 'controle' && <CentreControleTab getAuthConfig={getAuthConfig} sub={controleSub} onSub={setControleSub} />}

        {/* ===== TEMOIGNAGES ===== */}
        {activeTab === 'testimonials' && <TestimonialsTab getAuthConfig={getAuthConfig} />}
        {activeTab === 'visuels_ia' && role === 'full' && <VisuelsIATab getAuthConfig={getAuthConfig} />}

        {/* ===== MODELES DE MESSAGES ===== */}
        {activeTab === 'messages' && <MessageTemplatesTab getAuthConfig={getAuthConfig} />}

        {/* ===== CATALOGUE VOYAGES (form_destinations) ===== */}
        {activeTab === 'catalogue' && <FormDestinationsTab getAuthConfig={getAuthConfig} />}
        {activeTab === 'corbeille' && <TrashTripsTab getAuthConfig={getAuthConfig} />}
        {activeTab === 'tresorerie' && <Suspense fallback={<div className="text-center py-12 text-gray-400">Chargement…</div>}><TresorerieTab getAuthConfig={getAuthConfig} onGoTab={setActiveTab} /></Suspense>}
        {activeTab === 'feuille_route' && <Suspense fallback={<div className="text-center py-12 text-gray-400">Chargement…</div>}><FeuilleDeRouteTab getAuthConfig={getAuthConfig} onGoTab={setActiveTab} /></Suspense>}

        {/* ===== IMPORTS EN MASSE ===== */}
        {activeTab === 'imports' && <BulkImportTab getAuthConfig={getAuthConfig} />}

        {/* ===== ACCES DU SITE ===== */}
        {activeTab === 'access' && <AccessCodesTab getAuthConfig={getAuthConfig} />}

        {activeTab === 'staff' && <StaffDirectoryTab getAuthConfig={getAuthConfig} />}

        {/* ===== DINO PORTE-PAROLE WhatsApp (Iter 139) ===== */}
        {activeTab === 'dino' && <DinoTab getAuthConfig={getAuthConfig} />}

        {activeTab === 'stats' && <StatsTab getAuthConfig={getAuthConfig} role={role} />}
        {activeTab === 'depenses' && <DepensesTab getAuthConfig={getAuthConfig} />}
        {activeTab === 'import_recettes' && <ImportRecettesTab getAuthConfig={getAuthConfig} />}
        {activeTab === 'rattrapage_express' && <RattrapageExpressTab getAuthConfig={getAuthConfig} onGoTo={setActiveTab} />}
        {activeTab === 'assistant_ia' && <AssistantIATab getAuthConfig={getAuthConfig} />}
        {activeTab === 'caisse' && <CashRegisterTab getAuthConfig={getAuthConfig} />}
        {activeTab === 'ancv' && <AncvRegisterTab getAuthConfig={getAuthConfig} />}
        {activeTab === 'cheques_attente' && <PendingChequesTab getAuthConfig={getAuthConfig} />}
        {activeTab === 'pointage_banque' && <PointageBanqueTab getAuthConfig={getAuthConfig} />}
        {activeTab === 'sante_fiscale' && <DashboardFiscalTab getAuthConfig={getAuthConfig} />}
        {activeTab === 'moyens_paiement' && <MoyensPaiementTab getAuthConfig={getAuthConfig} />}
        {activeTab === 'prelevements' && <PrelevementsTab getAuthConfig={getAuthConfig} />}
        {activeTab === 'annulations' && <VoyagesAnnulesTab getAuthConfig={getAuthConfig} />}
        {activeTab === 'remises' && <RemisesTab getAuthConfig={getAuthConfig} />}
        {activeTab === 'permanences' && <PermanencesTab getAuthConfig={getAuthConfig} />}
        {/* Iter 512 (Kamel) — onglets « Journal échanges » et « Réponses types » supprimés */}
        {activeTab === 'urgent_reminders' && <UrgentRemindersTab getAuthConfig={getAuthConfig} />}
        {activeTab === 'excursions' && <ExcursionsTab getAuthConfig={getAuthConfig} role={role} />}
        {activeTab === 'espace' && <EspaceAdherentTab getAuthConfig={getAuthConfig} username={username} sub={espaceSub} onSub={setEspaceSub} />}
        {activeTab === 'taches' && <TachesTab getAuthConfig={getAuthConfig} />}
        {activeTab === 'tel_partages' && <TelephonesPartagesTab getAuthConfig={getAuthConfig} />}
        {/* Iter 378 — onglet du NOUVEAU système de plan bus rétabli (le libre-service, Iter 159) ;
            l'ancien système manuel (BusPlanTab Iter 120) est lui retiré de Gestion des voyages */}
        {activeTab === 'bus_plans' && <BusPlansAdmin getAuthConfig={getAuthConfig} />}
        {activeTab === 'brouillons' && <Suspense fallback={<div className="text-sm text-gray-500 p-4">Chargement…</div>}><BrouillonsPanel getAuthConfig={getAuthConfig} /></Suspense>}
        {activeTab === 'form_textes' && <Suspense fallback={<div className="text-sm text-gray-500 p-4">Chargement…</div>}><FormTextesPanel getAuthConfig={getAuthConfig} /></Suspense>}
        {activeTab === 'espagne' && <Suspense fallback={<div className="text-sm text-gray-500 p-4">Chargement…</div>}><EspagnePanel getAuthConfig={getAuthConfig} ouvert /></Suspense>}
        {activeTab === 'embarquement' && <EmbarquementTab getAuthConfig={getAuthConfig} />}
        {activeTab === 'marketing' && <EmailMarketingAdmin getAuthConfig={getAuthConfig} />}
        {/* Iter 156 — Échéancier accessible depuis Inscriptions */}
        {activeTab === 'echeancier' && <StatsTab getAuthConfig={getAuthConfig} role={role} initialView="echeancier" />}
        {activeTab === 'google_contacts' && <GoogleContactsSync getAuthConfig={getAuthConfig} />}
        {activeTab === 'coffre_fort' && <CoffreFortTab getAuthConfig={getAuthConfig} />}
        </Suspense>
      </div>
    </div>
    </AdminErrorBoundary>
  );
};

export default AdminDashboard;
